import React from 'react'
import { UserStream } from 'components/filters/FilterIcon'
import useUserOrdinalsBadge from 'hooks/useUserOrdinalsBadge'
import CommonEditor from '../shared/CommonEditor'
import StaticAnswerBody from './StaticAnswerBody'
import UserName from '../users/UserName'
import cn from 'classnames'
import styles from './post-answer.module.scss'
import { getImages, getText } from '../../lib/value'
import { mediaBucketsEnabled, naturalFeedEnabled } from 'lib/mediaBuckets'
import WithSelection from 'components/shared/WithSelection'
import AnswerTopControls from 'components/answers/AnswerTopControls'
import UnknownUserIcon from 'public/unknown-user-icon.png'
import { cacheAnswer, resolveUser } from 'state/cache'
import useStore from 'state/knovStore'
import { OVERSIZED_READONLY_DELTA_CHAR_LIMIT, getDeltaSummary } from 'components/answers/largeDelta'
import AnswerActionTokenPreview from 'components/answers/Answer/AnswerActionTokenPreview'
import HostInviteCard from 'components/answers/HostInviteCard'
import { scrubPrivateActionPromptsFromDelta } from 'components/answers/aiActionMetadataVisibility'
import api from 'api/api'
import { useOrdinalSettlementStatus } from 'hooks/useOrdinalSettlementStatus'
import {
    currentUserCanTradeOrdinals,
    currentUserHasOrdinalsMarket,
} from 'components/panels/ordinalMarketFeature'
import TeamIcon from 'components/users/TeamIcon'
import {
    ArrowUpCircle,
    Check,
    Gem,
    GripVertical,
    Info,
    Languages,
    ListMusic,
    MessageCircle,
    Pause,
    Play,
    Radio,
    Share2,
    Waves,
} from 'lucide-react'
import ShareTray from 'components/shared/ShareTray'
import OrdinalAudioCard from 'components/answers/FileEmbed/OrdinalAudioCard'
import SatsIcon from 'components/shared/SatsIcon'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { getAnswerExternalShareUrl, getAnswerShareCardUrl } from 'utils/shareUtils'
import {
    addTrackToPlaylist,
    openMediaDockRadio,
    playMediaDockTrack,
    removeTrackFromPlaylist,
} from 'components/shared/MediaDock/mediaDockEvents'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { subscribeRadioNow } from 'state/radioNowFeed'
import RadioBroadcastTransport from 'components/answers/RadioBroadcastTransport'
import RadioUpvalueButton, { RadioSongValue } from 'components/answers/RadioUpvalueButton'
import { hostedNativeMediaEnabled } from 'lib/hostedMediaPlayback'
import { useRadioSongTiming } from 'components/answers/radioSongTiming'
import AudioPlayerCore from 'components/answers/AudioPlayerCore'
import useInlineRadioTuneIn from 'components/shared/MediaDock/useInlineRadioTuneIn'
import {
    findMediaTrackForAnswer,
    isMediaDockRadioActive,
    isMediaKeyPlaying,
    pauseMediaKey,
    registerMediaTrack,
    subscribeToMediaDock,
    subscribeToMediaPlaybackState,
} from 'state/mediaPlaybackCoordinator'
import { radioTrackMediaKey } from 'components/shared/MediaDock/mediaDockRadio'
import RadioChanceRow from 'components/shared/MediaDock/RadioChanceRow'
import {
    getMediaDockTrackDisplayArtwork,
    getMediaDockTrackDisplayTitle,
} from 'components/shared/MediaDock/mediaDockTrackTitles'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import {
    radioAirplayMetaFromAnswer,
    radioAirplayStatusLabel,
    radioAttributionSummary,
    radioCardReplacesBody,
    radioPlayIndexRelation,
    radioPlayTitle,
    radioSongDisplayTitle,
    radioSongMedia,
    radioSongMediaKey,
    radioSongThreadQuestId,
    stationPosition,
} from 'components/answers/radioCardMedia'
import { getCachedAnswer } from 'state/cache'
import useOpenRight from 'refactor/hooks/useOpenRight'
import toast from 'utils/toast'
import { surfaceClickIsClaimed } from 'lib/suppressNextClick'
import { WalletFileCheckAction } from 'components/walletSetup/WalletSetupCard'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import AnswerButtons from 'components/answers/AnswerButtons'
import { EarningsShareTray } from 'components/modals/WalletDailyEarnings'
import ShareRewardsInfoTray from 'components/modals/ShareRewardsInfoTray'
import ShareToEarnLabel from 'components/shared/ShareToEarnLabel'
import { parseDailyAmountDisplay } from 'components/shared/dailyMoney'
import UserIcon from 'components/users/UserIcon'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import { dailyEarningsEnabled } from 'lib/dailyEarningsFeature'
import perms from 'lib/perms'
import { getViewerLanguage, shouldOfferTranslation } from 'lib/translationGate'
import { buildTranslatedAnswerBody } from 'lib/translatedAnswerBody'
import streamEmbedStyles from 'components/shared/stream-embed.module.scss'
import { isMobile } from 'lib/device'
import { blurFocusedNewAnswer } from 'state/imperativeApis/editorApi'
import { blurBeforeReadOnlyMoreExpansion } from 'components/answers/readOnlyMoreFocusBehavior'
import { isArtifactWrapperAnswer } from 'components/answers/ArtifactBadge'
import {
    hasRetainedFullAnswerBody,
    retainAnswerForRemount,
} from 'components/answers/answerInViewAnswer'
import { REPLY_CARD_BODYLESS_HEADER_HEIGHT_PX } from 'lib/staticGeometryChrome'
import { questPath } from 'lib/seoPaths'
import { USD_UNIT } from 'lib/bsv-util'

let clearNativeMoreFollowupClick = null

const suppressNativeMoreFollowupClick = pressEvent => {
    clearNativeMoreFollowupClick?.()

    const pressPoint = pressEvent.touches?.[0] || pressEvent.nativeEvent?.touches?.[0] || pressEvent
    const pressX = Number.isFinite(pressPoint.clientX) ? pressPoint.clientX : null
    const pressY = Number.isFinite(pressPoint.clientY) ? pressPoint.clientY : null
    const handleClick = event => {
        if (
            pressX != null &&
            pressY != null &&
            Math.hypot(event.clientX - pressX, event.clientY - pressY) > 24
        ) {
            return
        }

        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        clear()
    }
    const clear = () => {
        document.removeEventListener('click', handleClick, true)
        window.clearTimeout(clearTimer)
        if (clearNativeMoreFollowupClick === clear) clearNativeMoreFollowupClick = null
    }
    const clearTimer = window.setTimeout(clear, 500)

    clearNativeMoreFollowupClick = clear
    document.addEventListener('click', handleClick, true)
}

function UserStreamWithOrdinalsBadge(props) {
    const user = props?.user
    const enabled = !!user?.id
    const { hasBadge } = useUserOrdinalsBadge(user?.id, { enabled })
    const smallBadge = !props.isTitle
    return <UserStream {...props} showOrdinalsBadge={hasBadge} smallBadge={smallBadge} />
}

const normalizeActionTag = tag =>
    typeof tag === 'string' ? tag.trim().replace(/^!+/, '').toLowerCase() : ''

const streamInviteMetaFromAnswer = answer => {
    const meta = answer?.delta_json?.stream_invite
    return meta?.team_id ? meta : null
}

const dailyEarningsMetaFromAnswer = answer => {
    const meta = answer?.delta_json?.daily_earnings
    return meta?.day ? meta : null
}

const ordinalOfferMetaFromAnswer = answer => {
    const meta = answer?.delta_json?.ordinal_offer
    return meta?.order_id && meta?.ordinal_id ? meta : null
}

const ordinalOfferAnswerWithStatus = (answer, status) => {
    const meta = ordinalOfferMetaFromAnswer(answer)
    if (!meta || !status) return null

    return {
        ...answer,
        delta_json: {
            ...answer.delta_json,
            ordinal_offer: {
                ...meta,
                status,
            },
        },
    }
}

// Whether this answer renders as the earnings card instead of its text. The
// card twin REPLACES the body, so the renderer and the body-suppression
// check must share one predicate — otherwise a viewer who fails it gets an
// empty card instead of the plain-text post.
const dailyEarningsCardMeta = answer => {
    const meta = dailyEarningsMetaFromAnswer(answer)
    if (!meta) return null
    if (!dailyEarningsEnabled()) return null
    // Server-vouched markers only: delta_json (and is_system) are
    // client-writable, so a hand-written marker must not unlock the card
    // chrome for other viewers. The serialized daily_earnings_card flag
    // (Answer#daily_earnings_card) is the vouch for everyone; the owner-only
    // system-answer branch is the pre-flag rule, kept for notification JSON
    // cached before the flag existed (answer cache TTL) — it never shows a
    // card to anyone but its owner.
    if (answer?.daily_earnings_card === true) return meta
    if (answer?.is_system && perms.amAnswerOwner(answer)) return meta

    return null
}

const streamInviteAnswerWithStatus = (answer, status) => {
    const meta = streamInviteMetaFromAnswer(answer)
    if (!meta || !status) return null

    return {
        ...answer,
        delta_json: {
            ...answer.delta_json,
            stream_invite: {
                ...meta,
                status,
            },
        },
    }
}

const absoluteInviteMediaUrl = url => {
    if (!url) return null
    if (/^https?:\/\//i.test(url)) return url

    return `${window.knovApiUrl || ''}${url}`
}

const streamInviteTeamFromMeta = (meta, streamName) => {
    const teamImage = meta?.team_image?.url
        ? meta.team_image
        : meta?.team_image_url
        ? { url: meta.team_image_url }
        : null

    return {
        id: meta.team_id,
        name: streamName,
        public: meta?.private === false,
        team_image: teamImage,
        space_id: meta?.space_id,
    }
}

function StreamInvitePreview({ meta, streamName, openRight, openStreamLabel = null }) {
    const [hasBanner, setHasBanner] = React.useState(false)
    const [bannerKey] = React.useState(Date.now())
    const team = React.useMemo(() => streamInviteTeamFromMeta(meta, streamName), [meta, streamName])
    const streamPath = meta?.stream_path || (meta?.team_id ? `/stream/${meta.team_id}` : null)
    const bannerUrl = React.useMemo(() => {
        const fallbackUrl = meta?.team_id ? `/api/v1/teams/${meta.team_id}/banner?size=small` : null
        return absoluteInviteMediaUrl(meta?.banner_url || fallbackUrl)
    }, [meta?.banner_url, meta?.team_id])
    const bannerCheckUrl = bannerUrl
        ? `${bannerUrl}${bannerUrl.includes('?') ? '&' : '?'}t=${bannerKey}`
        : null

    React.useEffect(() => {
        setHasBanner(false)
    }, [bannerUrl])

    const preview = (
        <div className={cn(streamEmbedStyles.streamEmbed, styles.streamInviteEmbed)}>
            <div
                className={
                    hasBanner
                        ? streamEmbedStyles.bannerContainer
                        : streamEmbedStyles.bannerPlaceholder
                }
            >
                {hasBanner ? (
                    <img src={bannerCheckUrl} alt="" className={streamEmbedStyles.banner} />
                ) : (
                    <Waves className={streamEmbedStyles.wavesIcon} size={32} />
                )}
            </div>
            {bannerCheckUrl && (
                <img
                    key={`stream-invite-banner-check-${meta.team_id}-${bannerKey}`}
                    src={bannerCheckUrl}
                    onError={() => {
                        setHasBanner(false)
                    }}
                    onLoad={() => {
                        setHasBanner(true)
                    }}
                    style={{ display: 'none' }}
                    alt=""
                />
            )}
            <div className={streamEmbedStyles.streamBody}>
                <div className={streamEmbedStyles.streamLabel}>CHANNEL</div>
                <div className={streamEmbedStyles.streamHeader}>
                    <div className={streamEmbedStyles.streamIcon}>
                        <TeamIcon
                            team={team}
                            type="embed"
                            contextStyles={streamEmbedStyles.filterIcon}
                            tip={`${streamName} Channel`}
                            borderStyles={streamEmbedStyles.teamIconBorder}
                            initialStyles={streamEmbedStyles.teamIconInitial}
                        />
                    </div>
                    <div className={streamEmbedStyles.streamName}>
                        <div className={streamEmbedStyles.streamNameText}>{streamName}</div>
                    </div>
                </div>
            </div>
            {openStreamLabel && (
                <div className={styles.streamInviteOpenOverlay}>{openStreamLabel}</div>
            )}
        </div>
    )

    if (!streamPath) return preview

    const openInviteStream = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        if (openRight && meta?.team_id) openRight({ team_ids: [String(meta.team_id)] })
    }

    return (
        <button type="button" className={styles.streamInvitePreviewLink} onClick={openInviteStream}>
            {preview}
        </button>
    )
}

function StreamInviteActions({ answer, updateAnswer, openRight }) {
    const meta = streamInviteMetaFromAnswer(answer)
    const [status, setStatus] = React.useState(meta?.status || 'pending')
    const [busyAction, setBusyAction] = React.useState(null)
    const [showOpenStreamLabel, setShowOpenStreamLabel] = React.useState(false)
    const busyActionRef = React.useRef(null)
    const streamName = meta?.team_name || meta?.teamName || 'Channel invite'

    React.useEffect(() => {
        setStatus(meta?.status || 'pending')
        setBusyAction(null)
        busyActionRef.current = null
    }, [meta?.team_id, meta?.status])

    React.useEffect(() => {
        setShowOpenStreamLabel(false)
    }, [answer?.id, meta?.team_id])

    React.useEffect(() => {
        if (!showOpenStreamLabel) return undefined

        const timeoutId = window.setTimeout(() => setShowOpenStreamLabel(false), 3000)
        return () => window.clearTimeout(timeoutId)
    }, [showOpenStreamLabel])

    if (!meta?.team_id) return null

    const markNotificationActed = async () => {
        if (answer?.quest_id && answer?.id) await api.actOnNoti(answer.quest_id, answer.id)
    }

    const handleInviteAction = async (ev, action) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (busyActionRef.current || status !== 'pending') return

        busyActionRef.current = action
        setBusyAction(action)
        try {
            const result =
                action === 'accept'
                    ? await api.acceptTeamInvite(meta.team_id)
                    : await api.rejectTeamInvite(meta.team_id)
            await markNotificationActed()
            const nextStatus = result?.status || (action === 'accept' ? 'accepted' : 'rejected')
            const updatedAnswer = streamInviteAnswerWithStatus(answer, nextStatus)
            setStatus(nextStatus)
            setShowOpenStreamLabel(action === 'accept' && nextStatus === 'accepted')
            if (updatedAnswer) {
                cacheAnswer(updatedAnswer)
                updateAnswer?.(updatedAnswer)
            }
        } finally {
            busyActionRef.current = null
            setBusyAction(null)
        }
    }

    if (status === 'accepted') {
        return (
            <div className={styles.streamInvitePanel}>
                <StreamInvitePreview
                    meta={meta}
                    streamName={streamName}
                    openRight={openRight}
                    openStreamLabel={showOpenStreamLabel ? 'open channel' : null}
                />
                {!meta?.hide_status_label && (
                    <div className={styles.streamInviteStatus}>Accepted</div>
                )}
            </div>
        )
    }

    if (status === 'rejected') {
        return (
            <div className={styles.streamInvitePanel}>
                <StreamInvitePreview meta={meta} streamName={streamName} openRight={openRight} />
                <div className={styles.streamInviteStatus}>Declined</div>
            </div>
        )
    }

    return (
        <div className={styles.streamInvitePanel}>
            <StreamInvitePreview meta={meta} streamName={streamName} openRight={openRight} />
            <div className={styles.streamInviteActions}>
                <button
                    type="button"
                    className={cn(styles.streamInviteButton, styles.streamInviteDeclineButton)}
                    data-testid="stream-invite-decline"
                    aria-disabled={!!busyAction}
                    onClick={ev => handleInviteAction(ev, 'reject')}
                >
                    {busyAction === 'reject' ? 'Declining...' : 'Decline'}
                </button>
                <button
                    type="button"
                    className={cn(styles.streamInviteButton, styles.streamInviteAcceptButton)}
                    data-testid="stream-invite-accept"
                    aria-disabled={!!busyAction}
                    onClick={ev => handleInviteAction(ev, 'accept')}
                >
                    {busyAction === 'accept' ? 'Accepting...' : 'Accept'}
                </button>
            </div>
        </div>
    )
}

// Every post renders this; only the owner's own offer card owns any state,
// so the guard comes before the panel's hooks mount.
export function OrdinalOfferActions({ answer, updateAnswer }) {
    const meta = ordinalOfferMetaFromAnswer(answer)
    if (
        !meta?.order_id ||
        String(answer?.user_id || '') !== String(gon.currentUser?.id || '') ||
        !currentUserHasOrdinalsMarket()
    ) {
        return null
    }

    return <OrdinalOfferActionsPanel answer={answer} meta={meta} updateAnswer={updateAnswer} />
}

function OrdinalOfferActionsPanel({ answer, meta, updateAnswer }) {
    const [status, setStatus] = React.useState(meta.status || 'active')
    const [busyAction, setBusyAction] = React.useState(null)
    const [error, setError] = React.useState(null)
    const busyActionRef = React.useRef(null)

    React.useEffect(() => {
        setStatus(meta.status || 'active')
        setBusyAction(null)
        setError(null)
        busyActionRef.current = null
    }, [meta.order_id, meta.status])

    // Accepting starts a workflow that builds, may wait on the buyer's funds,
    // then broadcasts. Narrate it here the way the market rail does.
    const { settlementMessage, trackSettlement } = useOrdinalSettlementStatus()

    const applyStatus = nextStatus => {
        const updatedAnswer = ordinalOfferAnswerWithStatus(answer, nextStatus)
        setStatus(nextStatus)
        if (updatedAnswer) {
            cacheAnswer(updatedAnswer)
            updateAnswer?.(updatedAnswer)
        }
    }

    const handleAction = async (ev, action) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (busyActionRef.current || status !== 'active') return

        busyActionRef.current = action
        setBusyAction(action)
        setError(null)
        try {
            if (action === 'accept') {
                const result = await api.settleOrdinalOrder(
                    String(meta.ordinal_id),
                    String(meta.order_id),
                    { side: meta.side, price_sats: Number(meta.price_sats) },
                )
                trackSettlement(result)
                applyStatus(result?.order?.status || 'settling')
            } else {
                await api.declineOrdinalOrder(String(meta.ordinal_id), String(meta.order_id))
                applyStatus('declined')
            }
            if (answer?.quest_id && answer?.id) await api.actOnNoti(answer.quest_id, answer.id)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not update this offer.')
        } finally {
            busyActionRef.current = null
            setBusyAction(null)
        }
    }

    // What the last settlement attempt said when it ended without a sale. It
    // outlives the run that said it, so the owner reads it on any visit.
    const lastAttempt = meta.settlement_error

    if (status !== 'active') {
        const statusLabel =
            status === 'settling'
                ? settlementMessage || 'Sale in progress…'
                : status === 'settled'
                ? 'Offer accepted'
                : status === 'declined'
                ? 'Offer declined'
                : status === 'unfunded'
                ? "Offer closed — the buyer's wallet could not cover it"
                : status === 'failed'
                ? 'Offer closed — the sale could not complete'
                : status === 'expired'
                ? 'Offer expired'
                : 'Offer is no longer open'
        return (
            <div className={styles.ordinalOfferStatus}>
                {statusLabel}
                {status !== 'settling' && lastAttempt && (
                    <div className={styles.ordinalOfferError}>{lastAttempt}</div>
                )}
            </div>
        )
    }

    return (
        <div className={styles.ordinalOfferPanel}>
            {lastAttempt && !error && <div className={styles.ordinalOfferError}>{lastAttempt}</div>}
            {error && <div className={styles.ordinalOfferError}>{error}</div>}
            <div className={styles.streamInviteActions}>
                <button
                    type="button"
                    className={cn(styles.streamInviteButton, styles.streamInviteDeclineButton)}
                    disabled={!!busyAction}
                    aria-disabled={!!busyAction}
                    onClick={ev => handleAction(ev, 'decline')}
                >
                    {busyAction === 'decline' ? 'Declining…' : 'Decline'}
                </button>
                {currentUserCanTradeOrdinals() && (
                    <button
                        type="button"
                        className={cn(styles.streamInviteButton, styles.streamInviteAcceptButton)}
                        disabled={!!busyAction}
                        aria-disabled={!!busyAction}
                        onClick={ev => handleAction(ev, 'accept')}
                    >
                        {busyAction === 'accept' ? 'Accepting…' : 'Accept'}
                    </button>
                )}
            </div>
        </div>
    )
}

// "Aug 8, 2026" from the marker's ISO day. Split manually — new Date(iso)
// parses as UTC midnight and shifts a calendar day west of Greenwich.
const dailyEarningsDayLabel = isoDay => {
    const [year, month, day] = String(isoDay || '')
        .split('-')
        .map(Number)
    if (!year || !month || !day) return ''

    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

// One per-source row of the earnings card: icon, label, muted count, neon
// amount. The share-earnings row is a button (it opens the scheme explainer);
// the rest are static.
function DailyEarningsRow({ row }) {
    const { Icon } = row
    const className = cn(styles.dailyEarningsRow, row.muted && styles.dailyEarningsRowMuted)
    // Server strings lead sats with the ⚡ marker; the row draws the bolt
    // glyph in its place, like the stat above and the generated card's cells.
    const amount = parseDailyAmountDisplay(row.amount)
    const body = (
        <>
            <Icon aria-hidden="true" className={styles.dailyEarningsRowIcon} />
            <span className={styles.dailyEarningsRowLabel}>{row.label}</span>
            {row.count && <span className={styles.dailyEarningsRowCount}>· {row.count}</span>}
            {row.onClick && <Info aria-hidden="true" className={styles.dailyEarningsRowInfo} />}
            <span className={styles.dailyEarningsRowAmount}>
                {amount?.unit === 'sats' && (
                    <SatsIcon inline contextStyles={styles.dailyEarningsRowBolt} />
                )}
                <span>{amount ? amount.text : row.amount}</span>
            </span>
        </>
    )

    if (row.onClick) {
        return (
            <button
                type="button"
                className={className}
                data-testid={row.testId}
                onClick={row.onClick}
            >
                {body}
            </button>
        )
    }

    return (
        <div className={className} data-testid={row.testId}>
            {body}
        </div>
    )
}

// The daily-earnings card body: a CSS twin of the generated share card
// (identity row, neon amount, context line, per-source rows, brand footer —
// the same geometry as SocialCards::EarningsRenderer, scaled with
// container-query units). Replaces the wrapper answer's plain text body on
// the evening notification and on the share's public backing post alike;
// only the owner's notification carries the share pill, which sits after the
// rows so the brand line stays the card's bottom edge, and the tray it opens
// mints the day the notification announced (delta_json.daily_earnings.day),
// not "today", so a tap after the UTC rollover still shares the announced
// day. Exported for isolated harnesses.
export function DailyEarningsShareAction({ answer }) {
    const [shareTrayOpen, setShareTrayOpen] = React.useState(false)
    const [infoTrayOpen, setInfoTrayOpen] = React.useState(false)
    const meta = dailyEarningsCardMeta(answer)

    if (!meta) return null

    const isOwner = perms.amAnswerOwner(answer)
    // The share pill belongs to the owner's notification: minting is
    // self-scoped server-side, so on anyone else's card — or on the public
    // backing post, where the card is the post — it would share the VIEWER's day.
    const canShare = !!answer?.is_system && isOwner
    const cardUser = answer?.user || (isOwner ? gon.currentUser : null)
    const name = (cardUser?.name || '').trim() || 'A Treechat user'
    const count = Number(meta.upvalue_count) || 0
    // Server strings lead sats with the ⚡ marker; the card twin draws the
    // bolt glyph in its place, like the generated card does.
    const amount = parseDailyAmountDisplay(meta.amount_display)
    const dayLabel = dailyEarningsDayLabel(meta.day)

    const openShareTray = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        setShareTrayOpen(true)
    }

    const openInfoTray = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        setInfoTrayOpen(true)
    }

    // The per-source rows. A source that paid nothing renders muted for the
    // owner (every way to earn stays one glance away) and not at all for
    // other viewers of the public post; the share-view row follows the
    // program's own rule (the server sends its keys only for sharers in it,
    // even at $0). Markers minted before the split carry no upvalue or
    // ordinal keys and keep the upvalue count in the context line instead.
    const rows = []
    const upvalueSats = Number(meta.upvalue_sats) || 0
    if (typeof meta.upvalue_amount_display === 'string' && (isOwner || upvalueSats > 0)) {
        rows.push({
            key: 'upvalues',
            testId: 'daily-earnings-upvalues-row',
            Icon: ArrowUpCircle,
            label: 'Upvalues',
            count: count > 0 ? String(count) : null,
            amount: meta.upvalue_amount_display,
            muted: upvalueSats <= 0,
        })
    }
    const shareViewSats = Number(meta.share_view_sats) || 0
    if (typeof meta.share_view_amount_display === 'string' && (isOwner || shareViewSats > 0)) {
        const views = Number(meta.share_view_count) || 0
        rows.push({
            key: 'share-views',
            testId: 'daily-earnings-share-view-breakout',
            Icon: Share2,
            label: 'Share earnings',
            count: views > 0 ? `${views} ${views === 1 ? 'view' : 'views'}` : null,
            amount: meta.share_view_amount_display,
            muted: shareViewSats <= 0,
            onClick: openInfoTray,
        })
    }
    const ordinalSaleSats = Number(meta.ordinal_sale_sats) || 0
    if (typeof meta.ordinal_sale_amount_display === 'string' && (isOwner || ordinalSaleSats > 0)) {
        const sales = Number(meta.ordinal_sale_count) || 0
        rows.push({
            key: 'ordinal-sales',
            testId: 'daily-earnings-ordinal-sales-row',
            Icon: Gem,
            label: 'Ordinal sales',
            count: sales > 0 ? `${sales} ${sales === 1 ? 'sale' : 'sales'}` : null,
            amount: meta.ordinal_sale_amount_display,
            muted: ordinalSaleSats <= 0,
        })
    }
    // Stream-host revenue share (meta.host_sats rides delta_json.daily_earnings
    // alongside the rest of the marker) — only ever sent when it paid.
    const hostSats = Number(meta.host_sats) || 0
    if (hostSats > 0 && meta.host_amount_display) {
        rows.push({
            key: 'hosting',
            testId: 'daily-earnings-host-line',
            Icon: Radio,
            label: 'Hosting',
            count: isOwner ? 'your channels' : null,
            amount: meta.host_amount_display,
            muted: false,
        })
    }
    // For other viewers the split only earns its place when it says more
    // than the headline: a lone row would repeat the stat.
    const breakdown = isOwner || rows.length >= 2 ? rows : []
    // The upvalue count moves into its row; without that row (older markers,
    // or a public card showing no split) it stays in the context line.
    const countInRow = breakdown.some(row => row.key === 'upvalues')
    const contextLine =
        count > 0 && !countInRow
            ? `earned from ${count} ${count === 1 ? 'upvalue' : 'upvalues'} · ${dayLabel}`
            : `earned on Treechat · ${dayLabel}`

    return (
        <div className={styles.dailyEarningsSharePanel}>
            <div className={styles.dailyEarningsCard} data-testid="daily-earnings-noti-card">
                <div className={styles.dailyEarningsCardHeader}>
                    <div className={styles.dailyEarningsCardAvatar}>
                        <UserIcon
                            user={cardUser}
                            size="medium"
                            contextStyles={styles.dailyEarningsCardAvatarImage}
                        />
                    </div>
                    <span className={styles.dailyEarningsCardName}>{name}</span>
                </div>
                <div className={styles.dailyEarningsCardMiddle}>
                    <div className={styles.dailyEarningsCardStat}>
                        {amount?.unit === 'sats' && (
                            <SatsIcon inline contextStyles={styles.dailyEarningsCardBolt} />
                        )}
                        <span>{amount ? amount.text : meta.amount_display}</span>
                    </div>
                    <div className={styles.dailyEarningsCardContext}>{contextLine}</div>
                </div>
                {breakdown.length > 0 && (
                    <div
                        className={styles.dailyEarningsBreakdown}
                        data-testid="daily-earnings-breakdown"
                    >
                        {breakdown.map(row => (
                            <DailyEarningsRow key={row.key} row={row} />
                        ))}
                    </div>
                )}
                {canShare && (
                    <button
                        type="button"
                        className={styles.dailyEarningsShareButton}
                        data-testid="daily-earnings-noti-share"
                        onClick={openShareTray}
                    >
                        <Share2 size={15} aria-hidden="true" />
                        <ShareToEarnLabel fallback="Share today's earnings" />
                    </button>
                )}
                <div className={styles.dailyEarningsCardFooter}>
                    <span className={styles.dailyEarningsCardBrand}>
                        <TreechatIcon size={40} className={styles.dailyEarningsCardBrandIcon} />
                        Treechat
                    </span>
                    <span className={styles.dailyEarningsCardDomain}>treechat.com</span>
                </div>
            </div>
            <EarningsShareTray
                isOpen={shareTrayOpen}
                onDismiss={() => setShareTrayOpen(false)}
                day={meta.day}
            />
            <ShareRewardsInfoTray isOpen={infoTrayOpen} onDismiss={() => setInfoTrayOpen(false)} />
        </div>
    )
}

// Treechat Radio "now playing" heads carry their lottery receipt in
// action_tags.radio_play; song_answer_id is the embedded song.
const radioPlayMetaFromAnswer = answer => {
    const meta = answer?.action_tags?.radio_play
    return meta?.song_answer_id ? meta : null
}

// The dock track for the embedded song: whatever player already registered
// it, else registered here from the song's media (audio, or native video) so
// the button works before (or without) the inline player mounting.
const resolveRadioSongTrack = (songId, title) => {
    const song = getCachedAnswer(songId)
    const media = radioSongMedia(song)
    const registered = media
        ? findMediaTrackForAnswer(songId, media.kind)
        : findMediaTrackForAnswer(songId, 'audio') || findMediaTrackForAnswer(songId, 'video')
    if (registered) return registered
    if (!media) return null

    const mediaKey = radioSongMediaKey(media)
    if (!mediaKey) return null

    registerMediaTrack(mediaKey, {
        kind: media.kind,
        src: media.url,
        ...(media.poster_url ? { posterSrc: media.poster_url } : {}),
        title: title || media.name || (media.kind === 'video' ? 'Video' : 'Audio'),
        answerId: String(songId),
        questId: song?.quest_id || null,
    })
    return findMediaTrackForAnswer(songId, media.kind)
}

// The song's playlist key, read without registering the track: a press
// registers it (resolveRadioSongTrack); the button only needs to know whether
// the key is already in the viewer's playlist. Same key scheme as the post's
// own audio player, so a song added there reads as added here.
const radioSongPlaylistMediaKey = (answer, songId) => {
    if (!songId) return null
    const media = radioSongMedia(radioSongFromAnswer(answer, songId))
    const registered = media
        ? findMediaTrackForAnswer(songId, media.kind)
        : findMediaTrackForAnswer(songId, 'audio') || findMediaTrackForAnswer(songId, 'video')
    return registered?.mediaKey || (media ? radioSongMediaKey(media) : null)
}

const formatRadioChance = probability => {
    const percent = probability * 100
    if (percent > 0 && percent < 0.1) return '<0.1%'
    return `${Number(percent.toFixed(1))}%`
}

// The lottery receipt, rendered from action_tags.radio_play rather than
// post text: play number, the block that seeded the draw, the odds, and
// which weight table decided it.
const radioReceiptParts = meta => {
    const parts = []
    const playIndex = Number(meta.play_index)
    if (Number.isFinite(playIndex) && playIndex > 0) parts.push({ label: `Play #${playIndex}` })

    const blockHeight = Number(meta.block_height)
    if (Number.isFinite(blockHeight) && blockHeight > 0) {
        const hash = String(meta.block_hash || '')
        parts.push({
            label: `Block #${blockHeight}`,
            title: hash ? `Seeded by block hash ${hash}` : undefined,
        })
    } else if (meta.seed_source === 'clock') {
        parts.push({ label: 'Clock seed', title: 'No fresh block was available for this draw' })
    }

    const communityDraw = ['support', 'upvalues', 'playlists', 'discovery'].includes(
        meta.weight_source,
    )
    if (communityDraw) {
        const probability = Number(meta.weight) / Number(meta.total_weight)
        if (Number.isFinite(probability)) {
            parts.push({
                label: `${formatRadioChance(probability)} chance`,
                title: 'Combined chance across all selection categories at draw time',
            })
        }
        parts.push({
            label: {
                support: 'Community support pick',
                upvalues: 'Upvalues pick',
                playlists: 'Playlists pick',
                discovery: 'Discovery pick',
            }[meta.weight_source],
        })
    } else if (meta.novelty) {
        parts.push({
            label: 'Novelty pick',
            title: 'Drawn from the least-aired half of the rotation',
        })
    } else {
        const weight = Number(meta.weight)
        const total = Number(meta.total_weight)
        if (Number.isFinite(weight) && Number.isFinite(total) && total > 0) {
            const pct = Math.max(1, Math.round((weight / total) * 100))
            parts.push({
                label: `${pct}% odds`,
                title: `${weight.toLocaleString()} of ${total.toLocaleString()} weight`,
            })
        }
    }

    const source = {
        window: { label: 'Played-in sats', title: 'Picked by sats played-in since the last song' },
        credits: { label: 'All-time sats', title: 'Picked by all-time played-in sats' },
        pool: { label: 'Rotation', title: 'Picked by upvalue and playlist weight' },
    }[meta.weight_source]
    if (source) parts.push(source)

    return parts
}

// Both radio cards carry the song as an AnswerEmbed; Answer.jsx skips the
// generic quote embed for them, so this is the song's only rendering.
const radioSongFromAnswer = (answer, songId) => {
    const embedded = (answer?.embeds || []).find(embed => String(embed?.id) === String(songId))
    return getCachedAnswer(songId) || embedded || null
}

// A video song's transport outside the live card: the cards never mount a
// video of their own, so the button plays (or pauses) the song in the dock,
// which shows the picture. The time and seek line follow the dock.
function RadioDockPlayTransport({
    mediaKey,
    songId,
    title,
    src,
    knownDuration,
    actions = null,
    leading = null,
}) {
    const playing = useMediaKeyPlaying(mediaKey)
    const timing = useRadioSongTiming(mediaKey, src, knownDuration)
    return (
        <RadioBroadcastTransport
            isLive={false}
            tunedIn={false}
            position={timing.position}
            duration={timing.duration}
            progress={timing.progress}
            playing={playing}
            onReplay={() => {
                if (playing) {
                    pauseMediaKey(mediaKey)
                    return
                }
                const track = resolveRadioSongTrack(songId, title)
                if (track) playMediaDockTrack(track.mediaKey, 'radio_song_card')
            }}
            replayLabel={playing ? 'Pause' : 'Play'}
            mediaKey={mediaKey}
            answerId={songId}
            actions={actions}
            leading={leading}
        />
    )
}

// The song, dressed as the artwork-style audio card (the combined
// image+audio shape): cover art when the song has one — a video song's poster
// otherwise — the placeholder stage without either, transport docked on the
// scrim. Video plays in the dock, never in the card.
export function RadioSongCard({
    answer,
    songId,
    title,
    showByline = true,
    transport = undefined,
    stage = undefined,
    chromeLayer = undefined,
    // A list card with nothing around it: the upvalue rides in the player,
    // right of the playlist button, and the total at the row's left. The value
    // is the upvalue's source.
    upvalueInPlayer = undefined,
}) {
    const song = radioSongFromAnswer(answer, songId)
    const media = radioSongMedia(song)
    // Older station posts may predate server-authored embeds. Keep the song
    // identifiable even when there is no playable payload in this response.
    if (!media?.url) return <div className={styles.radioPlayingLine}>{title}</div>

    const mediaKey = radioSongMediaKey(media)
    const isVideo = media.kind === 'video'
    // A video song takes the regular player too while hosted playback is on (the
    // dock's picture parked over the card); otherwise it plays in the dock.
    const upvalueAction = upvalueInPlayer ? (
        <RadioUpvalueButton songId={songId} source={upvalueInPlayer} />
    ) : null
    const valueLeading = upvalueInPlayer ? <RadioSongValue songId={songId} /> : null
    const cardTransport =
        transport ||
        (isVideo && !hostedNativeMediaEnabled() ? (
            <RadioDockPlayTransport
                mediaKey={mediaKey}
                songId={songId}
                title={title}
                src={media.url}
                knownDuration={media.duration}
                actions={upvalueAction}
                leading={valueLeading}
            />
        ) : undefined)

    return (
        <div className={styles.radioSongCard} data-testid="radio-song-card">
            {showByline && song?.user?.name && (
                <div className={cn(styles.radioPlayingLine, styles.radioSongByline)}>
                    By
                    <UserIcon
                        user={song.user}
                        size="small"
                        contextStyles={styles.radioArtistAvatar}
                    />
                    <strong>@{song.user.name}</strong>
                </div>
            )}
            {/* The station's song is already a dock track (the radio pool registers
                it). Without an origin of its own the dock would adopt it the moment it
                starts here; registering makes the dock wait until the card actually
                leaves the screen. */}
            <OrdinalAudioCard
                src={media.url}
                mediaKey={mediaKey}
                kind={media.kind}
                title={
                    radioSongDisplayTitle(song, title) ||
                    media.name ||
                    (isVideo ? 'Video' : 'Audio')
                }
                previewUrl={song?.answer_image_url || media.poster_url || undefined}
                answerId={String(songId)}
                palette="purple"
                transport={cardTransport}
                playerActions={upvalueAction || undefined}
                playerLeading={valueLeading || undefined}
                stage={stage}
                // The poster, top left over the artwork.
                topLeft={
                    song?.user ? (
                        <UserIcon
                            user={song.user}
                            size="small"
                            contextStyles={styles.radioCardAvatar}
                        />
                    ) : undefined
                }
                chromeLayer={chromeLayer}
                // Every radio song card sits in a RadioSongThreadSurface, whose
                // hover reveals this pill, centered on the artwork.
                overlay={
                    <span className={styles.radioThreadHint} aria-hidden="true">
                        <MessageCircle aria-hidden="true" />
                        Open thread
                    </span>
                }
                persistPlayback
                canShare
                canDownload={
                    Boolean(song?.user?.id) &&
                    String(song.user.id) === String(window.gon?.currentUser?.id)
                }
            />
        </div>
    )
}

// Whether a play is the one the station is broadcasting right now. The slot's
// own window (carried in the receipt) answers it without a request — and
// crucially without asking /radio/now, whose read lazily ADVANCES the station
// and would push it past the very play being viewed.
const radioPlayIsLive = (meta, nowMs = Date.now()) => {
    const startedAt = Number(meta?.started_at)
    const endsAt = Number(meta?.ends_at)
    if (!Number.isFinite(startedAt) || !Number.isFinite(endsAt)) return false

    const seconds = nowMs / 1000
    return seconds >= startedAt && seconds < endsAt
}

function useIsLiveRadioPlay(meta) {
    const startedAt = Number(meta?.started_at)
    const endsAt = Number(meta?.ends_at)
    const [isLive, setIsLive] = React.useState(() => radioPlayIsLive(meta))

    React.useEffect(() => {
        setIsLive(radioPlayIsLive({ started_at: startedAt, ends_at: endsAt }))
        if (!Number.isFinite(endsAt)) return undefined

        // Flip the label off exactly when the slot ends, so a card open across
        // the boundary stops claiming the air.
        const msLeft = endsAt * 1000 - Date.now()
        if (msLeft <= 0 || msLeft > 6 * 60 * 60 * 1000) return undefined

        const timer = window.setTimeout(() => setIsLive(false), msLeft)
        return () => window.clearTimeout(timer)
    }, [startedAt, endsAt])

    return isLive
}

const RADIO_SONG_THREAD_UNAVAILABLE_MESSAGE = "Couldn't open the song's thread."

// Both radio cards are the song's surface, the way a market card is its
// ordinal's: the card and its headline open the song's own thread, where
// people talk about it. The play post itself is a station notice.
export function useOpenRadioSongThread(answer, songId, card) {
    const { openRight } = useOpenRight()

    return async (ev, source) => {
        ev.preventDefault()
        ev.stopPropagation()

        let outcome = 'failed'
        try {
            let questId = radioSongThreadQuestId(radioSongFromAnswer(answer, songId))
            if (!questId) {
                const song = await api.getAnswer(songId)
                if (song?.id) cacheAnswer(song)
                questId = radioSongThreadQuestId(song)
            }
            if (questId) {
                openRight({ questId, answerId: String(songId) }, { metricInputEvent: ev })
                outcome = 'opened'
            } else {
                toast.error(RADIO_SONG_THREAD_UNAVAILABLE_MESSAGE)
            }
        } catch (error) {
            console.error('Error opening the song thread from a radio card:', error)
            toast.error(RADIO_SONG_THREAD_UNAVAILABLE_MESSAGE)
        }

        // The card body used to bubble into the post's CLICK_ANSWER_GATE; this
        // is that click's replacement, so the surface stays measurable.
        window.logEv?.('CLICK_RADIO_CARD_THREAD', {
            source,
            outcome,
            card,
            song_answer_id: songId,
        })
    }
}

// The card's frame: a click anywhere opens the song's thread, except on
// chrome that owns its click (tuner, playlist, share, upvalue, the audio
// card's menu), which keeps its own activation. The footer (receipt,
// playlist, share) sits below the frame: it's about the play, not the song.
export function RadioSongThreadSurface({
    openSongThread,
    testId,
    footer = null,
    bodyClassName = undefined,
    children,
}) {
    return (
        <div className={styles.radioNowPlayingActions} data-testid={testId}>
            <div
                className={cn(
                    styles.radioThreadSurfaceBody,
                    styles.radioThreadSurface,
                    bodyClassName,
                )}
                onClick={ev => {
                    if (surfaceClickIsClaimed(ev)) {
                        ev.stopPropagation()
                        return
                    }
                    void openSongThread(ev, 'frame')
                }}
            >
                {children}
            </div>
            {footer}
        </div>
    )
}

// Full-width playlist + share actions shared by both radio cards.
function RadioActionRow({ answer, songId, title, shareLine, testIdPrefix }) {
    const [shareTrayOpen, setShareTrayOpen] = React.useState(false)
    const playlistMediaKey = radioSongPlaylistMediaKey(answer, songId)
    const inPlaylist = useMediaKeyInPlaylist(playlistMediaKey)
    const shareCardUrl = typeof window === 'undefined' ? null : getAnswerShareCardUrl(answer)

    // Mirrors the viewer's real playlist, wherever it changed: a press takes
    // the song out when it is in, and adds it otherwise.
    const handleTogglePlaylist = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        if (inPlaylist && playlistMediaKey) {
            removeTrackFromPlaylist(playlistMediaKey, testIdPrefix)
            return
        }
        const track = resolveRadioSongTrack(songId, title)
        if (track) addTrackToPlaylist(track.mediaKey, testIdPrefix)
    }

    const openShareTray = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        setShareTrayOpen(true)
    }

    return (
        <>
            <button
                type="button"
                className={styles.radioPlaylistButton}
                data-testid={`${testIdPrefix}-add-to-playlist`}
                onClick={handleTogglePlaylist}
                aria-pressed={inPlaylist}
            >
                {inPlaylist ? (
                    <Check size={15} aria-hidden="true" />
                ) : (
                    <ListMusic size={15} aria-hidden="true" />
                )}
                <span>{playlistMenuLabel(inPlaylist)}</span>
            </button>
            <button
                type="button"
                className={styles.radioShareButton}
                data-testid={`${testIdPrefix}-share`}
                onClick={openShareTray}
            >
                <Share2 size={15} aria-hidden="true" />
                <ShareToEarnLabel fallback="Share this play" />
            </button>
            <ShareTray
                canShareImageCard={Boolean(answer?.public) && Boolean(shareCardUrl)}
                closeButtonTestId={`${testIdPrefix}-share-tray-close`}
                dialogTestId={`${testIdPrefix}-share-tray`}
                isOpen={shareTrayOpen}
                onDismiss={() => setShareTrayOpen(false)}
                shareUrl={getAnswerExternalShareUrl(answer)}
                shareTitle={shareLine}
                shareText={shareLine}
                shareKind="post"
                shareCardId={answer?.id}
                shareCardUrl={shareCardUrl}
            />
        </>
    )
}

// The radio card's controls for a song outside a station post (the Artists
// profile): the upvalue row, and below the card the playlist + share actions.
// They act on the canonical song answer in place; none of them open a thread.
export function useRadioSongAnswer(songId) {
    const cachedSong = useCachedAnswer(songId)
    const resolveSong = React.useCallback(async () => {
        if (!songId) return null
        const cached = getCachedAnswer(songId)
        if (cached?.id) return cached
        const fetched = await api.getAnswer(songId)
        if (fetched?.id) cacheAnswer(fetched)
        return fetched || null
    }, [songId])
    React.useEffect(() => {
        if (!songId || cachedSong?.id) return
        resolveSong().catch(() => {})
    }, [cachedSong?.id, resolveSong, songId])
    return { song: cachedSong?.id ? cachedSong : null }
}

// `song` is the hydrated, canonical answer (see useRadioSongAnswer), read live
// from the cache so payments update the row.
export function RadioSongUpvalueRow({ song, source }) {
    if (!song?.id) return null

    return (
        <div
            className={styles.radioUpvalueRow}
            data-answer-interactive="true"
            data-testid="radio-upvalue-row"
            data-song-answer-id={song.id}
        >
            <AnswerButtons
                answer={song}
                upvalueSource={source}
                valueTestId="radio-song-value"
                className={styles.radioUpvalueControls}
                thin
            />
        </div>
    )
}

export function RadioSongActionRow({ song, title, testIdPrefix }) {
    if (!song?.id) return null

    return (
        <RadioActionRow
            answer={song}
            songId={song.id}
            title={title}
            shareLine={`On Treechat Music: ${title}`}
            testIdPrefix={testIdPrefix}
        />
    )
}

// The card that replaces a "now playing" head's body: the song's audio
// card, the receipt strip, then the full-width playlist + share actions.
export function RadioNowPlayingActions({ answer }) {
    const meta = radioPlayMetaFromAnswer(answer)
    const scheduledLive = useIsLiveRadioPlay(meta)
    const songId = meta?.song_answer_id ? String(meta.song_answer_id) : null

    // The money on this card goes to the song, not the station. The controls
    // row is bound to the song answer — the canonical, cached one, so a press
    // pays the artist, counts toward the song's draw, and the optimistic
    // update flows back through the same row. The play post itself stays a
    // system post with no controls of its own.
    const cachedSong = useCachedAnswer(songId)
    const resolveSong = React.useCallback(async () => {
        if (!songId) return null
        const cached = getCachedAnswer(songId)
        if (cached?.id) return cached
        const fetched = await api.getAnswer(songId)
        if (fetched?.id) cacheAnswer(fetched)
        return fetched || null
    }, [songId])
    React.useEffect(() => {
        if (!songId || cachedSong?.id) return
        resolveSong().catch(() => {})
    }, [cachedSong?.id, resolveSong, songId])

    // The station has one clock and one position for everyone; the card only
    // reflects it, ticking while the play is on the air.
    const song = songId ? radioSongFromAnswer(answer, songId) : null
    const media = radioSongMedia(song)
    const clock = useRadioStationClock(meta, scheduledLive, media?.duration)
    // A listener can report that an unknown-length file ended before its
    // scheduled fallback window. The shared feed is authoritative once it
    // shows a later play, so this card becomes an aired replay immediately.
    const isLive = scheduledLive && !clock?.stationEnded
    // Tuning in plays the song on air right here, like the Music header; the
    // dock takes the station once the card leaves the screen, or when this
    // play ends while tuned in (the song plays out even if the station's slot
    // closes first).
    const cardRef = React.useRef(null)
    const tuner = useInlineRadioTuneIn({
        cardRef,
        media,
        // The station position and the moment it was read, for the finish retry.
        clock: meta
            ? {
                  playIndex: Number(meta.play_index),
                  endsAt: Number(meta.ends_at),
                  position: clock?.position ?? null,
              }
            : null,
        live: isLive,
        position: clock?.position ?? null,
        receivedAt: Date.now(),
        dockTunedIn: useMediaDockRadioActive(),
        source: 'radio_now_playing_card',
        onPlayEnded: () => {
            tuner.endSession()
            openMediaDockRadio('radio_now_playing_card')
        },
    })
    const openSongThread = useOpenRadioSongThread(answer, songId, 'now_playing')

    if (!meta || !songId) return null

    const title = radioPlayTitle(answer)
    const receipt = radioReceiptParts(meta)
    const controlSong = cachedSong?.id ? cachedSong : song
    const artist = song?.user?.name || null

    // The button is a tuner, not a play button. The song is playing on the
    // station whether or not anyone presses it; pressing joins the broadcast
    // from the station's position or leaves it, and the station plays on
    // without you. Once the play has aired the station has moved on, so the
    // card is just the song: its own player plays it in place, and the dock
    // takes over only when the card leaves the screen.

    return (
        <RadioSongThreadSurface
            openSongThread={openSongThread}
            testId="radio-now-playing-actions"
            footer={
                <>
                    {receipt.length > 0 && (
                        <div className={styles.radioReceipt} data-testid="radio-receipt">
                            {receipt.map(part => (
                                <span
                                    key={part.label}
                                    className={styles.radioReceiptPart}
                                    title={part.title}
                                >
                                    {part.label}
                                </span>
                            ))}
                        </div>
                    )}
                    <RadioActionRow
                        answer={answer}
                        songId={songId}
                        title={title}
                        shareLine={`Now playing on Treechat Music: ${title}`}
                        testIdPrefix="radio-now-playing"
                    />
                </>
            }
        >
            <div
                className={cn(
                    styles.radioEyebrow,
                    isLive ? styles.radioEyebrowLive : styles.radioEyebrowAired,
                )}
                data-testid="radio-eyebrow"
            >
                {/* Radio's own past tense for a finished play — a station's
                    compliance record is the "as-aired log". "Off air" would be
                    wrong: that says the station stopped, not this song. The
                    status is the card's keyboard route to the song's thread. */}
                <button
                    type="button"
                    className={styles.radioThreadButton}
                    data-testid="radio-now-playing-open-thread"
                    aria-label={`${isLive ? 'On air' : 'Aired'}: open the song's thread`}
                    onClick={ev => void openSongThread(ev, 'header')}
                >
                    {isLive ? 'On air' : 'Aired'}
                </button>
                {/* The artist reads on the same line, at the same weight, so
                    "ON AIR  @handle" is one headline rather than a status with
                    a footnote. No "by": the avatar says it. */}
                {artist && (
                    <span className={styles.radioEyebrowArtist} data-testid="radio-eyebrow-artist">
                        <UserIcon
                            user={song?.user}
                            size="small"
                            contextStyles={styles.radioArtistAvatar}
                        />
                        <strong>@{artist}</strong>
                    </span>
                )}
            </div>
            <div ref={cardRef}>
                {tuner.playsHere && (
                    <AudioPlayerCore
                        ref={tuner.coreRef}
                        originRef={cardRef}
                        mediaKey={tuner.mediaKey}
                        src={media.url}
                        className={styles.radioInlineAudio}
                        onPlaybackStateChange={tuner.setInlinePlaying}
                    />
                )}
                <RadioSongCard
                    answer={answer}
                    songId={songId}
                    title={title}
                    showByline={false}
                    // A session still playing out its song keeps the tuner, so the
                    // listener can tune out where they tuned in.
                    transport={
                        isLive || tuner.listening ? (
                            <RadioBroadcastTransport
                                isLive
                                tunedIn={tuner.tunedIn}
                                position={clock?.position ?? null}
                                duration={clock?.duration ?? null}
                                progress={clock?.progress ?? null}
                                onTuneIn={tuner.tuneIn}
                                onTuneOut={tuner.tuneOut}
                            />
                        ) : undefined
                    }
                />
            </div>
            {controlSong?.id && (
                <div
                    className={styles.radioUpvalueRow}
                    data-answer-interactive="true"
                    data-testid="radio-upvalue-row"
                    data-song-answer-id={controlSong.id}
                >
                    <AnswerButtons
                        answer={controlSong}
                        upvalueSource="radio_now_playing"
                        resolveAnswer={cachedSong?.id ? undefined : resolveSong}
                        displayValueSats={
                            cachedSong?.id ? undefined : Number(song?.bsv_upvalue_amt || 0)
                        }
                        valueTestId="radio-song-value"
                        className={styles.radioUpvalueControls}
                        thin
                    />
                </div>
            )}
        </RadioSongThreadSurface>
    )
}

// A listener's credit post ("<sats> — @user is playing <song>"), coalesced
// per song and window. Renders as a card: the money first, market-card
// weight, then who is playing what, the song's audio card, and the same
// playlist + share actions as the now-playing card.
const radioAttributionMetaFromAnswer = answer => {
    // media_announcement is the current tag (one card per media). radio_attribution
    // is the earlier per-listener card, kept so existing posts still render.
    const meta = answer?.action_tags?.media_announcement || answer?.action_tags?.radio_attribution
    return meta?.song_answer_id ? meta : null
}

const radioAttributionListener = answer => {
    const ops = answer?.delta_json?.ops || []
    const mention = ops.find(op => op?.insert?.mention)?.insert?.mention
    if (mention?.value) {
        return { name: String(mention.value), link: mention.link || null }
    }
    const match = String(answer?.content || '').match(/@([A-Za-z0-9_]+)/)
    return match ? { name: match[1], link: null } : null
}

export function RadioAttributionActions({ answer }) {
    const { useUsd, formatSatsUsd } = useBsvDisplay()
    const meta = radioAttributionMetaFromAnswer(answer)
    const songId = meta ? String(meta.song_answer_id) : null
    const openSongThread = useOpenRadioSongThread(answer, songId, 'attribution')
    if (!meta) return null

    const sats = Math.max(0, Math.round(Number(meta.total_sats ?? meta.amount_sats) || 0))
    const usd = formatSatsUsd(sats)
    const listener = radioAttributionListener(answer)
    const { title, audience } = radioAttributionSummary(answer)
    const shareLine = String(answer?.content || '').trim() || 'Playing on Treechat Music'

    return (
        <RadioSongThreadSurface openSongThread={openSongThread} testId="radio-attribution-card">
            {useUsd && usd ? (
                <div className={cn(styles.radioAmount, styles.radioAmountUsd)}>
                    <span>{usd}</span>
                </div>
            ) : (
                <div className={styles.radioAmount}>
                    <SatsIcon inline contextStyles={styles.radioAmountIcon} />
                    <span>{sats.toLocaleString()}</span>
                    <span className={styles.radioAmountUnit}>sats</span>
                </div>
            )}
            {useUsd ? (
                <div className={styles.radioAmountEcho}>{sats.toLocaleString()} sats</div>
            ) : usd ? (
                <div className={styles.radioAmountEcho}>{usd}</div>
            ) : null}
            <div className={styles.radioPlayingLine}>
                {listener ? (
                    listener.link ? (
                        <a href={listener.link}>@{listener.name}</a>
                    ) : (
                        <strong>@{listener.name}</strong>
                    )
                ) : (
                    'Someone'
                )}
                {audience}
                <button
                    type="button"
                    className={cn(styles.radioThreadButton, styles.radioThreadTitle)}
                    data-testid="radio-attribution-open-thread"
                    aria-label={`${title}: open the song's thread`}
                    onClick={ev => void openSongThread(ev, 'header')}
                >
                    {title}
                </button>
            </div>
            <RadioSongCard answer={answer} songId={songId} title={title} />
            <RadioActionRow
                answer={answer}
                songId={songId}
                title={title}
                shareLine={shareLine}
                testIdPrefix="radio-attribution"
            />
        </RadioSongThreadSurface>
    )
}

// The song thread's airplay notice. "Played N times on Treechat Radio" is the
// post's own text; this line says WHEN — live now, or how long ago — from the
// latest slot's window in action_tags.radio_airplay. The thin embed under it
// is the latest room.
export function RadioAirplayMeta({ answer }) {
    const meta = radioAirplayMetaFromAnswer(answer)
    const isLive = useIsLiveRadioPlay(meta)
    if (!meta) return null
    const label = radioAirplayStatusLabel(meta, isLive)
    if (!label) return null
    return (
        <div
            className={styles.radioAirplayMeta}
            data-testid="radio-airplay-meta"
            data-live={isLive ? 'true' : 'false'}
        >
            {label}
        </div>
    )
}

// The "Up next" race reply (action_tags.radio_race): the contenders for the
// next draw, as the same rows as the dock's Radio tab, each cover a play
// button that hands the song to the dock. The tags carry playback fields;
// source posts use the shared embed cache and hydration queue.
// Current charts rank combined chances; older sectioned charts remain readable.
const radioRaceMetaFromAnswer = answer => {
    const meta = answer?.action_tags?.radio_race
    const sections = Array.isArray(meta?.sections)
        ? meta.sections.filter(
              section =>
                  section?.key && Array.isArray(section.contenders) && section.contenders.length,
          )
        : []
    return sections.length > 0 ? { ...meta, sections } : null
}

const radioContenderMediaKey = contender =>
    radioTrackMediaKey({
        kind: contender.kind,
        url: contender.url,
        attachmentId: contender.attachment_id,
        blobId: contender.blob_id,
    })

// Mirrors playback through any owner: the dock, or an inline player of the
// same song that happens to be on screen (the coordinator routes the play
// there and keeps the dock hidden).
function useMediaKeyPlaying(mediaKey) {
    const [playing, setPlaying] = React.useState(() =>
        mediaKey ? isMediaKeyPlaying(mediaKey) : false,
    )

    React.useEffect(() => {
        if (!mediaKey) return undefined
        const sync = () => setPlaying(isMediaKeyPlaying(mediaKey))
        sync()
        const unsubscribeDock = subscribeToMediaDock(sync)
        const unsubscribeState = subscribeToMediaPlaybackState(mediaKey, sync)
        return () => {
            unsubscribeDock()
            unsubscribeState()
        }
    }, [mediaKey])

    return playing
}

// One contender, rendered as the dock's Radio tab row — cover art, title,
// backing, chance — with the cover as a play button that hands the song to
// the dock, and the row opening the song in its thread. The song post
// hydrates through the shared embed queue for its title and cover.
function RadioRaceRow({ contender, maxChance, formatSats }) {
    const mediaKey = contender.url ? radioContenderMediaKey(contender) : null
    const playing = useMediaKeyPlaying(mediaKey)
    const songId = contender.song_answer_id ? String(contender.song_answer_id) : null
    const song = useCachedAnswer(songId)
    const { openRight } = useOpenRight()

    React.useEffect(() => {
        if (songId) queueAnswerHydration(songId, { lane: 'embed' })
    }, [songId])

    const track = {
        answerId: songId,
        title: contender.title,
        posterSrc: contender.poster_url || null,
    }
    const getAnswerById = () => song
    const title = getMediaDockTrackDisplayTitle(track, { getAnswerById }) || 'a song'
    const artworkUrl = getMediaDockTrackDisplayArtwork(track, { getAnswerById })
    const isVideo = contender.kind === 'video'

    const handleToggle = () => {
        if (!mediaKey) return
        if (playing) {
            pauseMediaKey(mediaKey)
            return
        }
        registerMediaTrack(mediaKey, {
            kind: isVideo ? 'video' : 'audio',
            src: contender.url,
            ...(contender.poster_url ? { posterSrc: contender.poster_url } : {}),
            title: contender.title || (isVideo ? 'Video' : 'Audio'),
            answerId: songId,
            questId: contender.quest_id ? String(contender.quest_id) : null,
        })
        playMediaDockTrack(mediaKey, 'radio_race')
    }

    const questId = contender.quest_id ? String(contender.quest_id) : null
    const handleOpen =
        questId && songId
            ? event => {
                  event.preventDefault()
                  event.stopPropagation()
                  openRight({ questId, answerId: songId })
              }
            : null

    const contributions = contender.contributions || {}
    return (
        <RadioChanceRow
            testId="radio-race-row"
            title={title}
            artworkUrl={artworkUrl}
            isVideo={isVideo}
            chance={contender.probability == null ? null : Number(contender.probability)}
            chanceSupport={Number(contributions.support) || 0}
            chanceDiscovery={Number(contributions.discovery) || 0}
            playlistCount={Number(contender.playlist_count) || 0}
            upvalueSats={Number(contender.sats) || 0}
            maxChance={maxChance}
            formatSats={formatSats}
            isActive={playing}
            activeLabel={playing ? 'Playing' : null}
            thumbAction={{ playing, onToggle: handleToggle, disabled: !mediaKey }}
            onOpen={handleOpen}
        />
    )
}

// While the play this reply belongs to is on the air, the chart is live: it
// follows the shared station feed and shows the odds as
// they stand now, not as they stood when the reply was last rewritten. Once
// the station has moved on (or the read fails) it settles on the stored
// reply, which is the chart as it ended.

// The live payload is keyed like the dock's tracks; the rows are keyed like
// the stored tag. One shape in, the tag's shape out.
const liveSectionsToTagShape = sections =>
    (sections || []).map(section => ({
        key: section.key,
        label: section.label,
        shares: section.shares,
        candidate_count: section.candidateCount,
        contenders: section.contenders.map(contender => ({
            song_answer_id: contender.answerId,
            quest_id: contender.questId,
            title: contender.title,
            sats: contender.sats,
            probability: contender.probability,
            playlist_count: contender.playlistCount,
            contributions: contender.contributions,
            upvalued_at: contender.upvaluedAt,
            url: contender.url,
            attachment_id: contender.attachmentId,
            blob_id: contender.blobId,
            duration: contender.duration,
            kind: contender.kind,
            poster_url: contender.posterUrl,
        })),
    }))

function useLiveRadioChart(meta) {
    const playIndex = Number(meta?.play_index)
    const [live, setLive] = React.useState(null) // { sections } while on air

    React.useEffect(() => {
        if (!Number.isFinite(playIndex)) return undefined
        // Once the station moves on there is nothing left to follow: let go of
        // the feed, so old replies do not keep the tab polling.
        let ended = false
        let unsubscribe = null
        const stop = () => {
            if (ended) return
            ended = true
            unsubscribe?.()
        }
        unsubscribe = subscribeRadioNow(payload => {
            if (ended) return
            if (!payload) {
                // A failed read does not mean this play ended. Fall back to the
                // stored chart for now; the feed tries again.
                setLive(null)
                return
            }
            const relation = radioPlayIndexRelation(payload.now?.playIndex, playIndex)
            if (relation === 'after') {
                // The station moved on: the stored reply is the chart as it ended.
                setLive(null)
                stop()
                return
            }
            if (relation !== 'same') {
                // A newly posted card can mount while the feed still retains
                // the preceding play. Wait for the in-flight/current read.
                setLive(null)
                return
            }
            setLive({
                sections: liveSectionsToTagShape(payload.contenders).filter(
                    section => section.contenders.length > 0,
                ),
            })
        })
        if (ended) unsubscribe()
        return () => stop()
    }, [playIndex])

    return live
}

// Whether this tab's dock is tuned into the station.
function useMediaDockRadioActive() {
    return React.useSyncExternalStore(subscribeToMediaDock, isMediaDockRadioActive, () => false)
}

// The station's clock for a play: position and length from the shared
// broadcast feed (each sample anchored on its arrival, so the clock ticks from
// local elapsed time), ticking once a second while the play is on the air. An
// aired play sits at its end.
function useRadioStationClock(meta, isLive, fileDuration) {
    const playIndex = Number(meta?.play_index)
    const startedAt = Number(meta?.started_at)
    const endsAt = Number(meta?.ends_at)
    // Until the feed's first sample lands, the play's own window is the length
    // — it is what the station scheduled, and what the feed will report — and
    // only a play without a window falls back to the file's length.
    const slotDuration = endsAt > startedAt ? endsAt - startedAt : null
    const fallbackDuration = slotDuration ?? fileDuration
    const [sample, setSample] = React.useState(null)
    const [endedPlayIndex, setEndedPlayIndex] = React.useState(null)
    const stationEnded = endedPlayIndex === playIndex
    const activeSample = sample?.playIndex === playIndex ? sample : null
    const [, setTick] = React.useState(0)

    React.useEffect(() => {
        if (!isLive || !Number.isFinite(playIndex)) return undefined
        let ended = false
        let unsubscribe = null
        const stop = () => {
            if (ended) return
            ended = true
            unsubscribe?.()
        }
        unsubscribe = subscribeRadioNow(
            payload => {
                if (ended) return
                // A failed read keeps the last sample; the clock ticks on from it.
                if (!payload) return
                const relation = radioPlayIndexRelation(payload.now?.playIndex, playIndex)
                if (relation === 'after') {
                    // The station can shorten an unknown-length slot when the
                    // file actually ends. Its feed outranks the post's original
                    // scheduled window, so the card must become "Aired" now.
                    setEndedPlayIndex(playIndex)
                    stop()
                    return
                }
                if (relation !== 'same') return
                setSample({
                    playIndex,
                    position: payload.now.position,
                    duration: payload.now.duration,
                    receivedAt: payload.receivedAt,
                })
                // Follows the play's clock only, not the odds chart.
            },
            { contenders: false },
        )
        if (ended) unsubscribe()
        return () => stop()
    }, [isLive, playIndex])

    React.useEffect(() => {
        if (!isLive || stationEnded) return undefined
        const timer = window.setInterval(() => setTick(tick => tick + 1), 1000)
        return () => window.clearInterval(timer)
    }, [isLive, stationEnded])

    if (!isLive || stationEnded) {
        const duration = Number(fallbackDuration) > 0 ? Number(fallbackDuration) : null
        return {
            position: duration,
            duration,
            progress: duration ? 1 : null,
            stationEnded,
        }
    }
    return {
        ...stationPosition({ startedAt, sample: activeSample, fallbackDuration }, Date.now()),
        stationEnded: false,
    }
}

export function RadioRaceCard({ answer }) {
    const meta = radioRaceMetaFromAnswer(answer)
    const live = useLiveRadioChart(meta)
    const { formatSatsAmount } = useBsvDisplay()
    if (!meta) return null

    const sections = live?.sections || meta.sections

    return (
        <div
            className={styles.radioRace}
            data-testid="radio-race-card"
            data-live={live ? 'true' : undefined}
        >
            <div className={styles.radioRaceHeading}>
                <span className={styles.radioRaceLabel}>
                    <span aria-hidden="true">🏁</span> Up next
                </span>
                <span className={styles.radioRaceSub}>
                    {sections.some(section => section.key === 'chances')
                        ? 'Chances for the next draw · Not a queue'
                        : 'Historical chart'}
                    {live && (
                        <span className={styles.radioRaceLive} data-testid="radio-race-live">
                            live
                        </span>
                    )}
                </span>
            </div>
            {sections.some(
                section => section.key === 'chances' && section.shares?.support != null,
            ) && (
                <div className={styles.radioRaceSubtitle}>
                    85% community support · 15% discovery
                    <details>
                        <summary>How the draw works</summary>
                        Every song needs both Upvalue and at least one other person’s playlist.
                        Community support multiplies the number of distinct playlisters by the
                        square root of Upvalue sats. Discovery chooses equally among the standing
                        songs heard the least in the past seven days, regardless of their support
                        amounts. A song rests for about two hours after it airs, and its poster
                        rests for the next three plays.
                    </details>
                </div>
            )}
            {sections.map(section => (
                <div
                    key={section.key}
                    className={styles.radioRaceSection}
                    data-testid={`radio-race-section-${section.key}`}
                >
                    <span className={styles.radioRaceSectionLabel}>
                        {section.label || section.key}
                        {section.key === 'chances' && section.shares && (
                            <>
                                {' '}
                                {section.shares.support != null ? (
                                    <>
                                        · {Math.round(section.shares.support * 100)}% Community
                                        support
                                    </>
                                ) : (
                                    <>
                                        · {Math.round(section.shares.upvalues * 100)}% Upvalues ·{' '}
                                        {Math.round(section.shares.playlists * 100)}% Playlists
                                    </>
                                )}{' '}
                                · {Math.round(section.shares.discovery * 100)}% Discovery
                            </>
                        )}
                    </span>
                    {section.contenders.map((contender, index) => (
                        <RadioRaceRow
                            key={`${section.key}-${contender.song_answer_id || index}`}
                            contender={contender}
                            maxChance={Math.max(
                                0,
                                ...section.contenders.map(row => Number(row.probability) || 0),
                            )}
                            formatSats={formatSatsAmount}
                        />
                    ))}
                    {section.key === 'chances' &&
                        section.candidate_count > section.contenders.length && (
                            <span className={styles.radioRaceSubtitle}>
                                {section.candidate_count - section.contenders.length} other songs ·{' '}
                                {formatRadioChance(
                                    Math.max(
                                        0,
                                        1 -
                                            section.contenders.reduce(
                                                (total, contender) => total + contender.probability,
                                                0,
                                            ),
                                    ),
                                )}{' '}
                                combined chance
                            </span>
                        )}
                </div>
            ))}
        </div>
    )
}

const hostRevenueMetaFromAnswer = answer => {
    const meta = answer?.delta_json?.host_revenue
    return meta?.team_id ? meta : null
}

// The stream host-revenue card (delta_json.host_revenue): the text says what
// this slice earned, the card says WHICH stream earned it and what it has paid
// in total. Reuses the stream preview from the invite cards so the tap target
// behaves the same everywhere — it opens the stream. Unlike the invite cards
// this one AUGMENTS the body instead of replacing it; there is nothing to act
// on, so the sentence stays the headline.
export function HostRevenueEarnings({ answer, openRight }) {
    const useUsd = useStore(state => state.bsvUnit === USD_UNIT)
    const meta = hostRevenueMetaFromAnswer(answer)
    if (!meta?.team_id) return null

    const streamName = meta.team_name || 'your channel'
    // Only worth a line once the stream has paid more than this notification
    // announces — on the very first reward it would just restate the sentence.
    const showTotal =
        !!meta.total_display && Number(meta.total_sats) > Number(meta.amount_sats || 0)

    return (
        <div className={styles.streamInvitePanel}>
            <StreamInvitePreview meta={meta} streamName={streamName} openRight={openRight} />
            {showTotal && (
                <div className={styles.hostRevenueTotal} data-testid="host-revenue-total">
                    <span
                        className={cn(
                            styles.hostRevenueTotalAmount,
                            !useUsd && styles.hostRevenueTotalAmountBitcoin,
                        )}
                    >
                        {meta.total_display}
                    </span>
                    <span>earned from this channel</span>
                </div>
            )}
        </div>
    )
}

const hostInviteMetaFromAnswer = answer => {
    const meta = answer?.delta_json?.host_invite
    return meta?.grant_id ? meta : null
}

const hostInviteAnswerWithStatus = (answer, status) => {
    const meta = hostInviteMetaFromAnswer(answer)
    if (!meta || !status) return null

    return {
        ...answer,
        delta_json: {
            ...answer.delta_json,
            host_invite: {
                ...meta,
                status,
            },
        },
    }
}

// The host-program invite (delta_json.host_invite): a personal offer to open a
// revenue-share stream. Accept moves the grant invited -> accepted and the card
// itself then offers both ways to start earning: create a stream, or switch
// rewards on for one the host already runs. "How host rewards work" opens the
// deal modal as an explanation only — the actions live here, on the card, so
// there is one place to act and one place to read. Mirrors StreamInviteActions.

export function HostInviteActions({ answer, updateAnswer }) {
    const meta = hostInviteMetaFromAnswer(answer)
    const [status, setStatus] = React.useState(meta?.status || 'pending')
    const [busyAction, setBusyAction] = React.useState(null)
    const busyActionRef = React.useRef(null)
    const openHostDealModal = useStore(state => state.actions.modalActions.openHostDealModal)
    const openCreateStreamModal = useStore(
        state => state.actions.modalActions.openCreateStreamModal,
    )
    const openHostStreamPickerModal = useStore(
        state => state.actions.modalActions.openHostStreamPickerModal,
    )
    const useUsd = useStore(state => state.bsvUnit === USD_UNIT)

    React.useEffect(() => {
        setStatus(meta?.status || 'pending')
        setBusyAction(null)
        busyActionRef.current = null
    }, [meta?.grant_id, meta?.status])

    if (!meta?.grant_id) return null

    const sharePercent = meta.share_bps ? meta.share_bps / 100 : 10
    const inviterName = meta.inviter_name || 'Treechat'
    const viewerIsInvitee =
        !meta.invitee_id || String(gon.currentUser?.id || '') === String(meta.invitee_id)
    const markNotificationActed = async () => {
        if (answer?.quest_id && answer?.id) await api.actOnNoti(answer.quest_id, answer.id)
    }

    const applyStatus = nextStatus => {
        const updatedAnswer = hostInviteAnswerWithStatus(answer, nextStatus)
        setStatus(nextStatus)
        if (updatedAnswer) {
            cacheAnswer(updatedAnswer)
            updateAnswer?.(updatedAnswer)
        }
    }

    const handleAction = async (ev, action) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (busyActionRef.current || status !== 'pending') return

        busyActionRef.current = action
        setBusyAction(action)
        try {
            if (action === 'accept') {
                const result = await api.acceptHostInvite()
                // Acceptance enrolls the host server-side. Keep the bootstrap
                // snapshot in sync so feature-gated stream controls are
                // available immediately, without requiring a page refresh.
                if (window.gon?.currentUser && Array.isArray(result?.features)) {
                    window.gon.currentUser.features = result.features
                }
                await markNotificationActed()
                // No modal on accept: the card flips to its accepted state,
                // which carries both ways to start earning.
                applyStatus('accepted')
            } else {
                await api.declineHostInvite()
                await markNotificationActed()
                applyStatus('declined')
            }
        } finally {
            busyActionRef.current = null
            setBusyAction(null)
        }
    }

    const handleLearnMore = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        openHostDealModal?.({ sharePercent, infoOnly: true })
    }

    const handleTurnOnExisting = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        openHostStreamPickerModal?.({ sharePercent })
    }

    const isLive = status === 'live'

    if (isLive || status === 'accepted') {
        const createStream = ev => {
            ev.preventDefault()
            ev.stopPropagation()
            openCreateStreamModal?.(null, null)
        }

        return (
            <HostInviteCard
                busyAction={busyAction}
                inviterName={inviterName}
                inviteeName={meta.invitee_name}
                onLearnMore={handleLearnMore}
                onPrimary={createStream}
                onTurnOnExisting={handleTurnOnExisting}
                sharePercent={sharePercent}
                status={status}
                teamName={meta.team_name}
                useUsd={useUsd}
                viewerIsInvitee={viewerIsInvitee}
            />
        )
    }

    return (
        <HostInviteCard
            busyAction={busyAction}
            inviterName={inviterName}
            inviteeName={meta.invitee_name}
            onAccept={viewerIsInvitee ? ev => handleAction(ev, 'accept') : undefined}
            onDecline={viewerIsInvitee ? ev => handleAction(ev, 'decline') : undefined}
            onLearnMore={handleLearnMore}
            sharePercent={sharePercent}
            status={status}
            teamName={meta.team_name}
            useUsd={useUsd}
            viewerIsInvitee={viewerIsInvitee}
        />
    )
}

const actionRequestId = action =>
    action?.id ||
    action?.client_id ||
    action?.ai_action_id ||
    action?.actionId ||
    action?.action_id ||
    action?.action_request_id ||
    null

const actionRequestPromptContent = action => {
    const prompt =
        action?.inputs?.text?.value ||
        action?.textInputValue ||
        action?.promptContent ||
        action?.prompt_content ||
        action?.prompt ||
        ''
    return typeof prompt === 'string' ? prompt.trim() : ''
}

const actionRequestMatchesToken = (action, attrs, tokenIndex, actionRequests) => {
    if (!action || !attrs) return false

    const tokenActionId = attrs.ai_action_id || attrs.actionId || null
    const requestActionId = actionRequestId(action)
    if (tokenActionId && requestActionId) return tokenActionId === requestActionId

    const tokenTag = normalizeActionTag(attrs.tag)
    const actionTag = normalizeActionTag(action.tag)
    if (!tokenTag || tokenTag !== actionTag) return false

    const sameTagRequests = actionRequests.filter(
        candidate => normalizeActionTag(candidate?.tag) === tokenTag,
    )
    if (sameTagRequests.length === 1) return sameTagRequests[0] === action

    return actionRequests[tokenIndex] === action
}

const actionRequestSourcesForEdit = (answer, draftAnswer) => {
    const draftActionRequests = Array.isArray(draftAnswer?.action_requests)
        ? draftAnswer.action_requests
        : []
    const answerActionRequests = Array.isArray(answer?.action_requests)
        ? answer.action_requests
        : []
    return [draftActionRequests, answerActionRequests].filter(
        actionRequests => actionRequests.length,
    )
}

const hydrateActionTokenPromptsForEdit = (deltaJson, answer, draftAnswer) => {
    const actionRequestSources = actionRequestSourcesForEdit(answer, draftAnswer)
    if (!deltaJson?.ops?.length || !actionRequestSources.length) return deltaJson

    let tokenIndex = -1
    let changed = false
    const ops = deltaJson.ops.map(op => {
        const attrs = op?.attributes?.actionTagAttrs
        if (!attrs || typeof attrs !== 'object') return op

        tokenIndex += 1
        if (typeof attrs.promptContent === 'string' && attrs.promptContent.trim()) return op

        const promptContent =
            actionRequestSources
                .map(actionRequests => {
                    const action = actionRequests.find(candidate =>
                        actionRequestMatchesToken(candidate, attrs, tokenIndex, actionRequests),
                    )
                    return actionRequestPromptContent(action)
                })
                .find(Boolean) || ''
        if (!promptContent) return op

        changed = true
        return {
            ...op,
            attributes: {
                ...op.attributes,
                actionTagAttrs: {
                    ...attrs,
                    promptContent,
                    textInputValue: promptContent,
                    hasTextInput: true,
                },
            },
        }
    })

    return changed ? { ...deltaJson, ops } : deltaJson
}

export default class PostAnswer extends React.PureComponent {
    COLLAPSE_POST_LENGTH = 400

    constructor(props) {
        super(props)
        this.state = {
            showKnovigator: false,
            showFullOversizedContent: false,
            loadingFullContent: false,
            expandAfterFullContentLoad: false,
            loadedFullAnswer: null,
            nativeCollapseOverflow: false,
        }
        this.richEditor = React.createRef()
        this.contentContainerRef = React.createRef()
        this.nativeCollapseContentNode = null
        this.nativeCollapseResizeObserver = null
        this.nativeCollapseMeasureFrame = null
        this.nativeMoreExpansionGestureTimer = null
        this.translationToggleGestureTimer = null
        this.handleMoreClick = this.handleMoreClick.bind(this)

        this.instanceId = `post-answer-${
            this.props.answer?.id || Math.random().toString(36).substring(2, 9)
        }`
    }

    // Add a click handler method for the more button
    handleMoreClick() {
        if (this.props.setTextIsCollapsed) {
            this.props.setTextIsCollapsed(false)
        }
    }

    // Read-only render form of the body delta: private AI-action prompts are
    // scrubbed for everyone (WYSIWYG — the owner sees the same bare token) so the
    // static body never renders a prompt that the token preview then collapses.
    // Reference-memoized because StaticAnswerBody regenerates HTML on value
    // identity changes.
    readOnlyBodyValue(editorValue, answer) {
        const actionRequests = Array.isArray(answer?.action_requests)
            ? answer.action_requests
            : null
        const cache = this._readOnlyBodyValueCache
        if (cache && cache.source === editorValue && cache.actionRequests === actionRequests) {
            return cache.result
        }

        const result = actionRequests
            ? scrubPrivateActionPromptsFromDelta(editorValue, actionRequests)
            : editorValue
        this._readOnlyBodyValueCache = { source: editorValue, actionRequests, result }
        return result
    }

    answerNeedsFullContentLoad() {
        const answer = this.props.answer
        if (!answer?.id || this.state.loadedFullAnswer) return false
        if (hasRetainedFullAnswerBody(answer.id)) return false
        if (answer.body_truncated) return true

        const renderedDelta =
            this.props.value || answer.delta_json || answer.nested_delta_json || {}
        const renderedTextLength = getText(renderedDelta)?.length || 0
        const localContentLength = Math.max(
            renderedTextLength,
            answer.content?.length || 0,
            answer.display_content?.length || 0,
            answer.content_preview?.length || 0,
        )
        const contentCharCount = Number(answer.content_char_count || 0)
        if (contentCharCount > localContentLength) return true
        if (localContentLength >= OVERSIZED_READONLY_DELTA_CHAR_LIMIT) return true

        const deltaJsonBytes = Number(answer.delta_json_bytes || 0)
        if (!deltaJsonBytes) return false

        try {
            return deltaJsonBytes > JSON.stringify(renderedDelta).length
        } catch (_error) {
            return true
        }
    }

    isUserSignedIn() {
        return document.getElementById('knovigator-body').getAttribute('data-user') === 'true'
    }

    voteContainerParent = React.createRef()

    // Methods to get content from uncontrolled editor
    getEditor = () => this.richEditor.current?.getEditor?.()
    getDeltaJson = () => this.getEditor()?.getContents()
    getContent = () => getText(this.getDeltaJson() || {}).trim()

    deleteActionTokenNodeAtIndex = index => {
        const editorInstance = this.getEditor()?.getEditor?.()
        const state = editorInstance?.state
        if (!state?.doc || !editorInstance?.view || !Number.isInteger(index) || index < 0) return

        /** @type {any} */
        let targetNode = null
        let targetPos = null
        let currentIndex = 0
        state.doc.descendants((node, pos) => {
            if (targetNode) return false
            if (node.type?.name !== 'actionTag') return true
            if (currentIndex === index) {
                targetNode = node
                targetPos = pos
                return false
            }
            currentIndex += 1
            return false
        })

        if (!targetNode || targetPos == null) return
        let deleteTo = targetPos + targetNode.nodeSize
        const nextChar = state.doc.textBetween(
            deleteTo,
            Math.min(deleteTo + 1, state.doc.content.size),
            '',
            '',
        )
        if (nextChar === ' ') deleteTo += 1

        editorInstance.view.dispatch(editorInstance.state.tr.delete(targetPos, deleteTo))
        editorInstance.view.focus()
    }

    handleActionTokenPromptChange = payload => {
        if (payload?.removed) {
            this.deleteActionTokenNodeAtIndex(payload.index)
        }
        this.props.onActionTokenPromptChange?.(payload)
    }

    componentDidMount() {
        // Store a reference to the bound handler
        this.boundMoreButtonHandler = this.handleMoreButtonClicked.bind(this)
        this.boundFullAnswerLoadedHandler = this.handleFullAnswerLoaded.bind(this)

        // Add event listener directly on document for better cross-view support
        document.addEventListener('more-button-clicked', this.boundMoreButtonHandler)
        document.addEventListener('full-answer-loaded', this.boundFullAnswerLoadedHandler)
        this.maybeLoadFullContentForExpandedPreview()
        this.scheduleNativeCollapseMeasure()
    }

    componentWillUnmount() {
        // Remove event listener when component unmounts
        document.removeEventListener('more-button-clicked', this.boundMoreButtonHandler)
        document.removeEventListener('full-answer-loaded', this.boundFullAnswerLoadedHandler)
        this.nativeCollapseResizeObserver?.disconnect()
        if (this.nativeCollapseMeasureFrame != null) {
            window.cancelAnimationFrame(this.nativeCollapseMeasureFrame)
        }
        if (this.nativeMoreExpansionGestureTimer != null) {
            window.clearTimeout(this.nativeMoreExpansionGestureTimer)
        }
        if (this.translationToggleGestureTimer != null) {
            window.clearTimeout(this.translationToggleGestureTimer)
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.answer?.id !== this.props.answer?.id) {
            this.setState(
                {
                    showFullOversizedContent: false,
                    loadingFullContent: false,
                    expandAfterFullContentLoad: false,
                    loadedFullAnswer: null,
                    nativeCollapseOverflow: false,
                },
                this.scheduleNativeCollapseMeasure,
            )
            return
        }

        this.maybeLoadFullContentForExpandedPreview()
        this.scheduleNativeCollapseMeasure()
    }

    maybeLoadFullContentForExpandedPreview() {
        if (this.props.editMode || this.props.isReplyPreview) return
        if (this.props.isEmbed) return
        if (this.props.textIsCollapsed) return
        if (!this.answerNeedsFullContentLoad()) return

        this.loadFullContent()
    }

    handleMoreButtonClicked = event => {
        // Only handle the event if it's for this specific instance
        if (event.detail.instanceId === this.instanceId) {
            this.expandFromMoreControl()

            // Return false to prevent any other handlers
            return false
        }
    }

    expandFromMoreControl() {
        blurBeforeReadOnlyMoreExpansion({ isMobile, blurFocusedNewAnswer })

        if (naturalFeedEnabled() && this.answerNeedsFullContentLoad()) {
            this.loadFullContent({ deferExpansion: true })
            return
        }

        if (this.props.setTextIsCollapsed) {
            this.props.setTextIsCollapsed(false)
        }
        if (this.answerNeedsFullContentLoad()) {
            this.loadFullContent()
        } else {
            this.setState({
                showFullOversizedContent: true,
            })
        }
    }

    requestNativeMoreExpansion = () => {
        // One tap can emit pointer/touch/click events; expand once and ignore the rest.
        if (this.nativeMoreExpansionGestureTimer != null) return

        this.expandFromMoreControl()
        this.nativeMoreExpansionGestureTimer = window.setTimeout(() => {
            this.nativeMoreExpansionGestureTimer = null
        }, 500)
    }

    handleNativeMoreClick = event => {
        this.stopNativeMoreEvent(event, { preventDefault: true })
        this.requestNativeMoreExpansion()
    }

    handleNativeMoreMouseDown = event => {
        blurBeforeReadOnlyMoreExpansion({ isMobile, blurFocusedNewAnswer, force: true })
        this.stopNativeMoreEvent(event, { preventDefault: true })
    }

    handleNativeMorePressStart = event => {
        blurBeforeReadOnlyMoreExpansion({ isMobile, blurFocusedNewAnswer, force: true })
        this.stopNativeMoreEvent(event)
        suppressNativeMoreFollowupClick(event)
        this.requestNativeMoreExpansion()
    }

    stopNativeMoreEvent = (event, options = {}) => {
        if (options.preventDefault) {
            event.preventDefault()
        }
        event.stopPropagation()
        event.nativeEvent?.stopImmediatePropagation?.()
    }

    // Same one-toggle-per-gesture guard as the native "more" control: a tap can
    // emit pointer/touch/click, and the body swap shifts layout under the ghost
    // click.
    requestTranslationToggle = () => {
        if (this.translationToggleGestureTimer != null) return

        this.props.onToggleTranslation?.()
        this.translationToggleGestureTimer = window.setTimeout(() => {
            this.translationToggleGestureTimer = null
        }, 500)
    }

    handleTranslationToggleClick = event => {
        this.stopNativeMoreEvent(event, { preventDefault: true })
        this.requestTranslationToggle()
    }

    handleTranslationToggleMouseDown = event => {
        this.stopNativeMoreEvent(event, { preventDefault: true })
    }

    handleTranslationTogglePressStart = event => {
        this.stopNativeMoreEvent(event)
        suppressNativeMoreFollowupClick(event)
        this.requestTranslationToggle()
    }

    setNativeCollapseContentNode = node => {
        if (this.nativeCollapseContentNode === node) return

        this.nativeCollapseResizeObserver?.disconnect()
        this.nativeCollapseResizeObserver = null
        this.nativeCollapseContentNode = node

        if (node && typeof ResizeObserver !== 'undefined') {
            this.nativeCollapseResizeObserver = new ResizeObserver(
                this.scheduleNativeCollapseMeasure,
            )
            this.nativeCollapseResizeObserver.observe(node)
        }

        this.scheduleNativeCollapseMeasure()
    }

    scheduleNativeCollapseMeasure = () => {
        if (typeof window === 'undefined') return
        if (this.nativeCollapseMeasureFrame != null) {
            window.cancelAnimationFrame(this.nativeCollapseMeasureFrame)
        }
        this.nativeCollapseMeasureFrame = window.requestAnimationFrame(() => {
            this.nativeCollapseMeasureFrame = null
            this.measureNativeCollapseOverflow()
        })
    }

    measureNativeCollapseOverflow = () => {
        const node = this.nativeCollapseContentNode
        const nextOverflow = Boolean(node && node.scrollHeight > node.clientHeight + 1)
        if (this.state.nativeCollapseOverflow !== nextOverflow) {
            this.setState({ nativeCollapseOverflow: nextOverflow })
        }
    }

    handleFullAnswerLoaded(event) {
        const fullAnswer = event.detail?.answer
        if (!fullAnswer?.id || fullAnswer.id !== this.props.answer?.id) return

        this.applyFullAnswer(fullAnswer)
    }

    /* truncate a quill delta object to only return number of chars specified */
    truncateDelta(delta, maxChars = this.COLLAPSE_POST_LENGTH) {
        if (!delta || typeof delta === 'string' || !Array.isArray(delta.ops)) return delta

        let charCount = 0
        let truncatedDelta = { ops: [] }

        for (const op of delta.ops) {
            if (charCount >= maxChars) break

            if (typeof op.insert === 'string') {
                let remainingChars = maxChars - charCount
                let text = op.insert.slice(0, remainingChars)
                charCount += text.length

                truncatedDelta.ops.push({ ...op, insert: text })

                if (charCount === maxChars) {
                    // Add ellipsis with standard text formatting
                    truncatedDelta.ops.push({ insert: '... ' })
                }
            } else {
                truncatedDelta.ops.push(op)
            }
        }

        return truncatedDelta
    }

    async loadFullContent({ deferExpansion = false } = {}) {
        const answer = this.props.answer
        if (!answer?.id || this.state.loadingFullContent) return

        this.setState({
            ...(deferExpansion ? {} : { showFullOversizedContent: true }),
            loadingFullContent: true,
            expandAfterFullContentLoad: deferExpansion,
        })

        try {
            const fullAnswer = await api.getAnswer(answer.id, { full_body: true })
            if (!fullAnswer?.id) throw new Error('Full answer payload missing')

            retainAnswerForRemount(fullAnswer)
            cacheAnswer(fullAnswer)
            this.props.updateAnswer?.(fullAnswer)
            document.dispatchEvent(
                new CustomEvent('full-answer-loaded', {
                    detail: {
                        answer: fullAnswer,
                    },
                }),
            )
        } catch (error) {
            console.error('Failed to load full answer content', error)
            this.setState({
                loadingFullContent: false,
                expandAfterFullContentLoad: false,
            })
        }
    }

    applyFullAnswer(fullAnswer) {
        const expandAfterFullContentLoad = this.state.expandAfterFullContentLoad
        this.setState(
            {
                loadedFullAnswer: fullAnswer,
                loadingFullContent: false,
                expandAfterFullContentLoad: false,
                showFullOversizedContent: true,
            },
            () => {
                if (expandAfterFullContentLoad) {
                    this.props.setTextIsCollapsed?.(false)
                }
                if (fullAnswer.delta_json) {
                    this.richEditor.current?.setContents?.(fullAnswer.delta_json, 'api')
                }
            },
        )
    }

    // The live TipTap editor: always used in edit mode, and as the fallback
    // when StaticAnswerBody can't serialize the content.
    renderLiveEditor(editorValue, editorContentKey, isEmbed, answer, COLLAPSE_POST_LENGTH) {
        return (
            <CommonEditor
                key={editorContentKey}
                ref={this.richEditor}
                isEmbed={isEmbed}
                type={this.props.isTitle ? 'title' : 'answer'}
                editorTestId={this.props.editMode ? 'answer-edit-editor' : undefined}
                defaultValue={editorValue}
                collapseEnabled={false}
                collapseMaxChars={COLLAPSE_POST_LENGTH}
                collapseInstanceId={this.instanceId}
                collapseShowMoreControl={!isEmbed}
                placeholder="say something..."
                setEmbeds={this.props.setEmbeds}
                setEmbedsLoading={this.props.setEmbedsLoading}
                setLinkPreviews={this.props.setLinkPreviews}
                embedsFingerprint={this.props.embedsFingerprint}
                autoSave={true}
                postHandler={this.props.savePost}
                imgHandler={this.props.imgHandler}
                readOnly={!this.props.editMode}
                readOnlyActionTokenPrompts={this.props.editMode}
                onActionTokenPromptChange={this.handleActionTokenPromptChange}
                answer={answer}
                panel={this.props.panel}
                uiId={this.props.uiId}
                onEditorLoad={editor => {
                    if (this.props.editMode) {
                        // Position cursor at the end when entering edit mode
                        const length = editor.getLength() || 0
                        editor.setSelection(length, 0)
                    }
                    this.scheduleNativeCollapseMeasure()
                }}
            />
        )
    }

    render() {
        const { textIsCollapsed, COLLAPSE_POST_LENGTH } = this.props
        const answer = this.state.loadedFullAnswer || this.props.answer
        const renderUser = resolveUser(answer?.user, answer?.user_id)
        const isEmbed = this.props.isEmbed
        const renderValue =
            this.props.editMode || !this.state.loadedFullAnswer
                ? this.props.value || answer.delta_json
                : answer.delta_json || this.props.value
        const deltaSummary = getDeltaSummary(renderValue)
        const isOversizedReadonlyContent =
            !answer?.body_truncated &&
            !this.props.editMode &&
            deltaSummary.oversized &&
            !this.props.isReplyPreview
        const retainedExpandedFullContent =
            naturalFeedEnabled() &&
            textIsCollapsed === false &&
            hasRetainedFullAnswerBody(answer?.id)
        const showOversizedPreview =
            isOversizedReadonlyContent &&
            !this.state.showFullOversizedContent &&
            !retainedExpandedFullContent
        //console.log('RENDER POST ANSWER', answer.id, answer.delta_json)
        // Artifact wrapper bodies are just the canonical content URL — the
        // ArtifactBadge header renders the artwork, so the text body stays
        // hidden outside edit mode.
        // A title's content column starts at the card edge, not at the byline's
        // text column — so a quote left in it sat under the AVATAR rather than
        // under the name. Titles put it in the byline, where a reply's already
        // reads from. Reply anatomy (post lens, reply cards) already indents its
        // content column, so it keeps the quote where it is.
        const quoteInByline = !!(
            this.props.isTitle &&
            this.props.sentQuoteLine &&
            !this.props.postLens &&
            !this.props.replyCard
        )
        const showContent =
            this.props.editMode ||
            (!isArtifactWrapperAnswer(answer) &&
                (deltaSummary.hasText ||
                    deltaSummary.hasImage ||
                    (isEmbed &&
                        (getText(answer.nested_delta_json) ||
                            getImages(answer.nested_delta_json)))))

        const embedPostContainerStyles = isEmbed ? styles.embedPostContainer : null
        const topPadding = isEmbed ? styles.embedPadding : null
        // Artifact wrapper cards drop the system byline ("Treechat · 31s" is
        // the wrapper row's author/created_at, not the artifact's) — the
        // ArtifactBadge strip above and the meta row under the artwork carry
        // the card's identity instead.
        const showUser = !this.props.blog && !this.props.isStory && !isArtifactWrapperAnswer(answer)

        const stylesUserIcon = cn(this.props.isTitle ? styles.titleUserIcon : styles.userIcon)

        const isAgent = answer.message_type === 'chatgpt' || answer.message_type === 'agent'

        // Long-term collapse behavior:
        // - For collapsed views (embedded posts or collapsed answers), keep the rendered preview
        //   intact and let native layout clip it with max-height/overflow.
        // - For oversized read-only posts, render through the same character boundary used
        //   to classify the Delta as too large so the "see more" load point is consistent.
        // - For reply previews, keep the existing Delta truncation (no "more" control).
        const shouldNativeCollapse =
            !!renderValue &&
            !this.props.editMode &&
            !this.props.isReplyPreview &&
            (this.props.isEmbed || textIsCollapsed)

        const truncatedValue =
            renderValue && (this.props.isReplyPreview || showOversizedPreview)
                ? this.truncateDelta(
                      renderValue,
                      showOversizedPreview
                          ? OVERSIZED_READONLY_DELTA_CHAR_LIMIT
                          : this.props.isReplyPreview && this.props.maxCharacters
                          ? this.props.maxCharacters
                          : COLLAPSE_POST_LENGTH,
                  )
                : renderValue
        const editorValue = this.props.editMode
            ? hydrateActionTokenPromptsForEdit(truncatedValue, answer, this.props.draftAnswer)
            : truncatedValue

        const systemUser = this.props.systemUser
        const answerCreatedAt =
            systemUser && answer.action_tags?.quoted
                ? this.props.quest?.notification_context?.created_at || answer.created_at
                : answer.created_at
        const isStory = this.props.isStory
        const isIndentLayout = this.props.isIndentLayout
        const isTitle = this.props.isTitle

        const userHasParagraphFeature = this.props.userHasParagraphFeature
        const newUserReplying = this.props.newUserReplying

        const hasParagraphStyles =
            userHasParagraphFeature && !isTitle && !this.props.replyCard && !newUserReplying
        const editorContentKey = `post-editor-${answer?.id || 'unknown'}-${
            this.props.editMode ? 'edit' : this.state.loadedFullAnswer ? 'full' : 'preview'
        }`
        const showNativeMoreControl =
            !isEmbed &&
            !this.props.editMode &&
            !this.props.isReplyPreview &&
            (showOversizedPreview ||
                this.answerNeedsFullContentLoad() ||
                (shouldNativeCollapse && this.state.nativeCollapseOverflow))

        // Flag-gated: reserve the "more" button's 30px slot whenever the post is collapse-
        // eligible, instead of letting the async overflow measurement toggle it in and out.
        // Without this, every long post oscillates ±30px on each mount (button absent on first
        // paint, appears after the rAF measure), which shakes the virtualized scroll container.
        const reserveNativeMoreSlot =
            mediaBucketsEnabled() &&
            !isEmbed &&
            !this.props.editMode &&
            !this.props.isReplyPreview &&
            (showOversizedPreview || this.answerNeedsFullContentLoad() || shouldNativeCollapse)

        // "Show translation" swaps the text body in place. The toggle lives in
        // the same below-content controls row as the native "more" button, so it
        // stays visible (and the row height stays stable) while the content
        // above it is clipped by the collapse.
        const translationText =
            typeof this.props.translationText === 'string' && this.props.translationText.trim()
                ? this.props.translationText
                : null
        const translatedViewActive = Boolean(
            this.props.showTranslation && !this.props.editMode && translationText,
        )
        const showTranslationControl =
            !isEmbed &&
            !this.props.editMode &&
            !this.props.isReplyPreview &&
            !isStory &&
            typeof this.props.onToggleTranslation === 'function' &&
            this.isUserSignedIn() &&
            (this.props.showTranslation ||
                this.props.translationLoading ||
                shouldOfferTranslation(answer, getViewerLanguage()))
        const translationToggleLabel = this.props.translationLoading
            ? 'translating…'
            : translatedViewActive
            ? 'show original'
            : this.props.translationError
            ? 'translation failed — retry'
            : 'show translation'

        // The translated prose renders through the same StaticAnswerBody pipeline
        // while retaining inline image/video ops from the original post.
        const readOnlyBodyValue = this.readOnlyBodyValue(editorValue, answer)
        const bodyValue = translatedViewActive
            ? buildTranslatedAnswerBody(readOnlyBodyValue, translationText)
            : readOnlyBodyValue
        const bodyContentKey = translatedViewActive
            ? `${editorContentKey}-translated-${this.props.translationTargetLang || 'x'}`
            : editorContentKey

        return (
            <div
                className={cn(styles.postAnswerComp, topPadding)}
                data-natural-flow-geometry-pending={
                    naturalFeedEnabled() && this.state.loadingFullContent ? 'true' : undefined
                }
                data-natural-feed-geometry-pending={
                    naturalFeedEnabled() && this.state.loadingFullContent ? 'true' : undefined
                }
            >
                <div className={cn(styles.flexContainer)}>
                    <div className={cn(styles.postContainer, embedPostContainerStyles)}>
                        <div
                            className={cn(
                                styles.userAndControlsContainer,
                                this.props.replyCard && styles.replyCardUserAndControlsContainer,
                                quoteInByline && styles.quotingUserAndControlsContainer,
                                // dm_chat: a run's later messages drop the face and byline;
                                // their hover controls stay, floated at the row's top right.
                                this.props.dmChatRun === 'cont' && styles.dmChatContByline,
                            )}
                            style={
                                this.props.replyCard &&
                                !showContent &&
                                this.props.dmChatRun !== 'cont'
                                    ? { minHeight: REPLY_CARD_BODYLESS_HEADER_HEIGHT_PX }
                                    : undefined
                            }
                        >
                            {showUser && (
                                <div
                                    className={cn(
                                        styles.userContainer,
                                        this.props.replyCard && styles.replyCardUserContainer,
                                    )}
                                >
                                    <div
                                        className={cn(
                                            styles.userIconContainer,
                                            isIndentLayout && styles.indentUserIconContainer,
                                            isTitle && styles.titleUserIconContainer,
                                        )}
                                    >
                                        {!this.props.isMuted && this.props.showDragHandle && (
                                            <div
                                                ref={el => {
                                                    this.props.dragConnector?.(el)
                                                    this.props.dragHandleRefSetter?.(el)
                                                }}
                                                className={styles.dragHandle}
                                                data-dnd-handle="true"
                                                onClickCapture={ev => {
                                                    // Let the answer ignore grip clicks without blocking
                                                    // the drag listeners attached below.
                                                    ev.preventDefault()
                                                }}
                                                {...(this.props.dragHandleListeners || {})}
                                            >
                                                <GripVertical size={14} />
                                            </div>
                                        )}
                                        {this.props.isMuted ? (
                                            <div
                                                className={cn(
                                                    styles.userIcon,
                                                    this.props.isTitle && styles.titleUserIcon,
                                                    this.props.replyCard &&
                                                        styles.replyCardUserIcon,
                                                )}
                                            >
                                                <img src={UnknownUserIcon} alt="Muted user" />
                                            </div>
                                        ) : (
                                            <UserStreamWithOrdinalsBadge
                                                filter={this.props.filter}
                                                panel={this.props.panel}
                                                user={renderUser || answer.user}
                                                agentIcon={isAgent}
                                                virtualUserIcon={answer.virtual_user_icon_url}
                                                showKnovigator={this.props.showKnovigator}
                                                isTitle={this.props.isTitle}
                                                size="small"
                                                clickable={!isAgent}
                                                contextStyles={cn(
                                                    styles.userIcon,
                                                    this.props.isTitle && styles.titleUserIcon,
                                                    this.props.replyCard &&
                                                        styles.replyCardUserIcon,
                                                    isAgent && styles.agentIcon,
                                                    isAgent &&
                                                        isIndentLayout &&
                                                        styles.indentAgentIcon,
                                                    hasParagraphStyles && styles.paragraphIcon,
                                                )}
                                            />
                                        )}
                                    </div>

                                    <div
                                        className={cn(
                                            styles.userNameContainer,
                                            hasParagraphStyles && styles.userNameContainerParagraph,
                                            quoteInByline && styles.quotingUserNameContainer,
                                        )}
                                    >
                                        {this.props.isMuted ? (
                                            <div
                                                className={cn(
                                                    styles.userName,
                                                    hasParagraphStyles && styles.paragraph,
                                                    this.props.isTitle && styles.titleUserName,
                                                    quoteInByline && styles.quotingUserName,
                                                )}
                                            >
                                                {/* Empty username for muted users */}
                                            </div>
                                        ) : (
                                            <UserName
                                                user={renderUser || answer.user}
                                                answer={answer}
                                                isTitle={this.props.isTitle}
                                                virtualUserName={answer.virtual_user_name}
                                                transcriptSegmentInfo={
                                                    answer?.transcript?.segment_info
                                                }
                                                answerCreatedAt={answerCreatedAt}
                                                // The timestamp is the permalink.
                                                // Title cards link their thread,
                                                // including post-lens titles that
                                                // use reply anatomy;
                                                // replies link their CHILD quest —
                                                // every post is also the root of
                                                // its own thread, and these are
                                                // the tree's thread->subthread
                                                // edges in the rendered DOM.
                                                permalinkHref={
                                                    this.props.isTitle || this.props.postLens
                                                        ? questPath(
                                                              this.props.quest?.slug ||
                                                                  this.props.quest?.id,
                                                          )
                                                        : questPath(
                                                              answer.child_quests?.[0]?.slug ||
                                                                  answer.child_quests?.[0]?.id,
                                                          )
                                                }
                                                contextStyles={cn(
                                                    styles.userName,
                                                    hasParagraphStyles && styles.paragraph,
                                                    this.props.isTitle && styles.titleUserName,
                                                    quoteInByline && styles.quotingUserName,
                                                )}
                                                showKnovigator={this.props.showKnovigator}
                                                agentModel={
                                                    answer.message_type === 'agent'
                                                        ? answer.agent_model
                                                        : null
                                                }
                                            />
                                        )}
                                        {quoteInByline && (
                                            <div className={styles.bylineQuoteLine}>
                                                {this.props.sentQuoteLine}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!isStory && !systemUser && !isEmbed && (
                                <div
                                    className={cn(
                                        styles.answerTopControlsContainer,
                                        this.props.isTitle &&
                                            styles.titleAnswerTopControlsContainer,
                                        quoteInByline && styles.quotingAnswerTopControlsContainer,
                                    )}
                                >
                                    <AnswerTopControls
                                        isTitle={this.props.isTitle}
                                        isBsv={this.props.isBsv}
                                        answer={this.props.answer}
                                        quest={this.props.quest}
                                        hover={this.props.answerHover}
                                        toggleEdit={this.props.toggleEdit}
                                        showControlsPopup={this.props.showControlsPopup}
                                        toggleControlsPopup={this.props.toggleControlsPopup}
                                        archiveConfirm={this.props.archiveConfirm}
                                        toggleArchiveConfirm={this.props.toggleArchiveConfirm}
                                        getPublicLink={this.props.getPublicLink}
                                        copyContent={this.props.copyContent}
                                        openShareTray={this.props.openShareTray}
                                        isUserSignedIn={this.props.isUserSignedIn}
                                        editMode={this.props.editMode}
                                    />
                                </div>
                            )}
                        </div>

                        {!showContent && this.props.sentQuoteLine && !quoteInByline && (
                            // Bodyless answers (media-only) skip contentContainer
                            // entirely; the quote line still needs its indent.
                            <div
                                className={cn(
                                    styles.contentContainer,
                                    isIndentLayout && styles.indentContentContainer,
                                    this.props.isTitle && styles.titleContentContainer,
                                    this.props.replyCard && styles.replyCardContentContainer,
                                )}
                            >
                                {this.props.sentQuoteLine}
                            </div>
                        )}
                        {showContent && (
                            <div
                                ref={this.contentContainerRef}
                                className={cn(
                                    styles.contentContainer,
                                    !this.props.editMode && styles.answerActionTokenPreviewScope,
                                    isIndentLayout && styles.indentContentContainer,
                                    this.props.isTitle && styles.titleContentContainer,
                                    this.props.replyCard && styles.replyCardContentContainer,
                                    this.props.editMode && styles.editContentContainer,
                                    this.props.isReplyPreview && styles.replyPreviewContent,
                                    // contentContainer is a flex ROW; the quote
                                    // line needs its own line above the body.
                                    this.props.sentQuoteLine &&
                                        !quoteInByline &&
                                        styles.hasSentQuoteLine,
                                )}
                            >
                                {(!quoteInByline && this.props.sentQuoteLine) || null}
                                {this.props.isMuted && !this.props.editMode ? (
                                    <div className={styles.mutedContent}>
                                        <span className={styles.mutedUserName}>
                                            {renderUser?.name ? `@${renderUser.name}` : 'User'}{' '}
                                            (muted)
                                        </span>
                                        <button
                                            className={styles.showMutedButton}
                                            onClick={e => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                                this.props.onRevealMuted()
                                            }}
                                        >
                                            Show
                                        </button>
                                    </div>
                                ) : (
                                    <WithSelection
                                        editMode={this.props.editMode}
                                        isTitle={this.props.isTitle}
                                    >
                                        <div
                                            ref={this.setNativeCollapseContentNode}
                                            data-static-geometry-intentional-clip={
                                                shouldNativeCollapse ? 'true' : undefined
                                            }
                                            className={cn(
                                                styles.nativeCollapseContent,
                                                shouldNativeCollapse &&
                                                    styles.nativeCollapseContentCollapsed,
                                                showNativeMoreControl &&
                                                    styles.nativeCollapseContentOverflow,
                                            )}
                                        >
                                            {this.props.editMode ? (
                                                this.renderLiveEditor(
                                                    editorValue,
                                                    editorContentKey,
                                                    isEmbed,
                                                    answer,
                                                    COLLAPSE_POST_LENGTH,
                                                )
                                            ) : dailyEarningsCardMeta(answer) ||
                                              hostInviteMetaFromAnswer(answer) ||
                                              radioCardReplacesBody(answer, isEmbed) ? null : ( // The card twin / host-invite hero / radio cards below replace the text body.
                                                <StaticAnswerBody
                                                    key={bodyContentKey}
                                                    value={bodyValue}
                                                    answer={answer}
                                                    type={this.props.isTitle ? 'title' : 'answer'}
                                                    isEmbed={isEmbed}
                                                    uiId={this.props.uiId}
                                                    onRendered={() =>
                                                        this.scheduleNativeCollapseMeasure()
                                                    }
                                                    renderFallback={() =>
                                                        this.renderLiveEditor(
                                                            bodyValue,
                                                            bodyContentKey,
                                                            isEmbed,
                                                            answer,
                                                            COLLAPSE_POST_LENGTH,
                                                        )
                                                    }
                                                />
                                            )}
                                            {!this.props.editMode && (
                                                <StreamInviteActions
                                                    answer={answer}
                                                    updateAnswer={this.props.updateAnswer}
                                                    openRight={this.props.openRight}
                                                />
                                            )}
                                            {!this.props.editMode && (
                                                <OrdinalOfferActions
                                                    answer={answer}
                                                    updateAnswer={this.props.updateAnswer}
                                                />
                                            )}
                                            {!this.props.editMode && (
                                                <HostInviteActions
                                                    answer={answer}
                                                    updateAnswer={this.props.updateAnswer}
                                                />
                                            )}
                                            {!this.props.editMode && (
                                                <DailyEarningsShareAction answer={answer} />
                                            )}
                                            {!this.props.editMode && (
                                                <WalletFileCheckAction
                                                    answer={answer}
                                                    isOwner={perms.amAnswerOwner(answer)}
                                                />
                                            )}
                                            {!this.props.editMode && !isEmbed && (
                                                <RadioNowPlayingActions answer={answer} />
                                            )}
                                            {!this.props.editMode && !isEmbed && (
                                                <RadioAttributionActions answer={answer} />
                                            )}
                                            {!this.props.editMode && !isEmbed && (
                                                <RadioRaceCard answer={answer} />
                                            )}
                                            {!this.props.editMode && !isEmbed && (
                                                <RadioAirplayMeta answer={answer} />
                                            )}
                                            {!this.props.editMode && (
                                                <HostRevenueEarnings
                                                    answer={answer}
                                                    openRight={this.props.openRight}
                                                />
                                            )}
                                        </div>
                                        {(reserveNativeMoreSlot ||
                                            showNativeMoreControl ||
                                            showTranslationControl) &&
                                            !this.state.loadingFullContent && (
                                                <div
                                                    className={styles.contentControlsRow}
                                                    style={
                                                        reserveNativeMoreSlot
                                                            ? { height: 30, overflow: 'hidden' }
                                                            : undefined
                                                    }
                                                >
                                                    {showNativeMoreControl && (
                                                        <button
                                                            type="button"
                                                            className={styles.nativeMoreButton}
                                                            data-testid="post-answer-native-more"
                                                            data-answer-id={answer?.id}
                                                            onClickCapture={
                                                                this.handleNativeMoreClick
                                                            }
                                                            onMouseDownCapture={
                                                                this.handleNativeMoreMouseDown
                                                            }
                                                            onPointerDownCapture={
                                                                this.handleNativeMorePressStart
                                                            }
                                                            onTouchStartCapture={
                                                                this.handleNativeMorePressStart
                                                            }
                                                        >
                                                            more
                                                        </button>
                                                    )}
                                                    {showTranslationControl && (
                                                        <button
                                                            type="button"
                                                            className={cn(
                                                                styles.nativeMoreButton,
                                                                styles.translationToggleButton,
                                                            )}
                                                            data-testid="post-answer-translation-toggle"
                                                            data-answer-id={answer?.id}
                                                            onClickCapture={
                                                                this.handleTranslationToggleClick
                                                            }
                                                            onMouseDownCapture={
                                                                this
                                                                    .handleTranslationToggleMouseDown
                                                            }
                                                            onPointerDownCapture={
                                                                this
                                                                    .handleTranslationTogglePressStart
                                                            }
                                                            onTouchStartCapture={
                                                                this
                                                                    .handleTranslationTogglePressStart
                                                            }
                                                        >
                                                            <Languages size={14} />
                                                            {translationToggleLabel}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        {this.state.loadingFullContent && (
                                            <span className={styles.fullContentLoading}>
                                                Loading...
                                            </span>
                                        )}
                                        {!this.props.editMode && !this.props.isReplyPreview && (
                                            <AnswerActionTokenPreview
                                                answer={answer}
                                                containerRef={this.contentContainerRef}
                                                quest={this.props.quest}
                                            />
                                        )}
                                    </WithSelection>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}

// The Music header's tuner reads the dock's tune-in state (RadioHeaderCard).
export { useMediaDockRadioActive }
